import type { AccountFailoverEvent, AdminUserDetailTab, AdminView, ApiKey, BillingFundingEvent, BillingModelRates, BridgeFailoverEvent, ChannelOverride, ChatGptAccount, EntitlementTransaction, HealthStatus, Member, MemberEntitlement, ModelBillingRate, ModelBillingRateRow, ModelUsagePeriods, MonthlyPassOperations, OauthStatus, Order, PaginationMetadata, PaginationState, PricingPlan, ProviderBridgeInstance, ProviderChannel, ProviderInstance, PublicModel, PublicModelManagementStatus, RechargeRevenueSummary, RequestLog, UsageFundingAllocation, UsageLimitConfig, UsageSummary, User, WalletTransaction, WalletTransactionSummary } from "@/types";
import { api } from "@/api";
import { renderAdminGuide } from "@/docs";
import { els, state } from "@/state";
import {
  escapeHtml, fmtInt, fmtNum, formatMicrocreditsAsCredits, formatTime, fromDatetimeLocal,
  isDeletedMember, parseUserRoles, pill, saveStoredPlainApiKeys,
  shortId, toDatetimeLocal
} from "@/utils";
import { handleCreateUserKey } from "@/views/app";
import { showUsageLimitEditor } from "@/views/usageLimitEditor";
import { renderModelUsageCharts } from "@/views/modelUsageChart";

// ===== 数据加载 =====

/** 加载 admin 首屏核心数据(不含大列表) */
export async function loadAll(): Promise<void> {
  if (!state.user) {
    try {
      const meData = await api<{ member: User; csrfToken: string }>("/api/auth/me");
      state.user = meData.member;
      state.csrfToken = meData.csrfToken || "";
    } catch {
      state.user = null;
      state.csrfToken = "";
    }
  }

  const membersData = await api<{ members: Member[] }>("/admin/members");

  state.members = (membersData.members || []).filter(
    (member) => !isDeletedMember(member)
  );

  renderAdminPage();
}

/** 加载 admin 扩展数据(不含大日志/流水列表) */
export async function loadAdminData(): Promise<void> {
  const results = await Promise.allSettled([
    loadAdminUsersData(),
    loadAdminChannelsData(),
    loadAdminProductsData(),
    loadAdminOrdersData(),
    loadAdminEntitlementsData(),
    loadAdminRevenueSummary()
  ]);
  results.forEach((result, index) => {
    if (result.status === "rejected") {
      const labels = ["admin users", "admin channels", "admin products", "admin orders", "admin entitlements", "admin revenue"];
      console.error(`${labels[index]} load failed:`, result.reason);
    }
  });
  void loadAdminSecondaryData();
}

export async function loadAdminUsersData(): Promise<void> {
  const [data, captureData] = await Promise.all([
    api<{ users: User[] }>("/api/admin/users"),
    api<{ policy: { enabled: boolean }; runtimeEnabled: boolean }>("/api/admin/interaction-capture-policy")
  ]);
  state.adminUsers = data.users || [];
  state.adminInteractionCapturePolicy = {
    enabled: captureData.policy?.enabled === true,
    runtimeEnabled: captureData.runtimeEnabled === true
  };
}

export async function setGlobalInteractionCapture(enabled: boolean): Promise<void> {
  try {
    const data = await api<{ policy: { enabled: boolean }; runtimeEnabled: boolean }>(
      "/api/admin/interaction-capture-policy",
      {
        method: "PATCH",
        body: JSON.stringify({ enabled })
      }
    );
    state.adminInteractionCapturePolicy = {
      enabled: data.policy?.enabled === true,
      runtimeEnabled: data.runtimeEnabled === true
    };
    toast(enabled ? "采集总开关已开启。" : "采集总开关已关闭。", "success");
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function setUserInteractionCapture(userId: string, enabled: boolean): Promise<void> {
  try {
    await api(`/api/admin/users/${encodeURIComponent(userId)}/interaction-capture`, {
      method: "PATCH",
      body: JSON.stringify({ enabled })
    });
    toast(enabled ? "用户交互采集已开启。" : "用户交互采集已关闭。", "success");
    await loadAdminUsersData();
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function setKeyInteractionCapture(apiKeyId: string, mode: string): Promise<void> {
  const enabled = mode === "inherit" ? null : mode === "on" ? true : mode === "off" ? false : undefined;
  if (enabled === undefined) {
    toast("Key 采集策略无效。", "error");
    return;
  }
  try {
    await api(`/api/admin/api-keys/${encodeURIComponent(apiKeyId)}/interaction-capture`, {
      method: "PATCH",
      body: JSON.stringify({ enabled })
    });
    toast("Key 交互采集策略已保存。", "success");
    await loadAdminUsersData();
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function loadAdminChannelsData(): Promise<void> {
  const [channelsData, providerInstancesData, instancesData, accountsData, failoverData, overridesData] = await Promise.all([
    api<{ channels: ProviderChannel[] }>("/api/admin/channels"),
    api<{ providerInstances: ProviderInstance[] }>("/api/admin/provider-instances"),
    api<{ bridgeInstances: ProviderBridgeInstance[] }>("/api/admin/bridge-instances"),
    api<{ accounts: ChatGptAccount[] }>("/api/admin/accounts"),
    api<{ events: BridgeFailoverEvent[] }>("/api/admin/bridge-failover-events?limit=50"),
    api<{ overrides: ChannelOverride[] }>("/api/admin/channel-overrides?limit=50")
  ]);
  state.adminChannels = channelsData.channels || [];
  state.adminProviderInstances = providerInstancesData.providerInstances || [];
  state.adminBridgeInstances = instancesData.bridgeInstances || [];
  state.chatGptAccounts = accountsData.accounts || [];
  state.adminBridgeFailoverEvents = failoverData.events || [];
  state.adminChannelOverrides = overridesData.overrides || [];
}

export async function loadAdminRequestLogs(): Promise<void> {
  state.requestLogsLoading = true;
  try {
    const data = await api<{ requestLogs: RequestLog[] }>("/admin/request-logs?limit=80");
    state.requestLogs = data.requestLogs || [];
    state.requestLogsLoaded = true;
  } finally {
    state.requestLogsLoading = false;
  }
}

export async function openTodayErrorRequests(page = 1): Promise<void> {
  state.todayErrorRequestsOpen = true;
  state.todayErrorRequestsLoading = true;
  renderTodayErrorRequests();
  try {
    const data = await api<{ requestLogs: RequestLog[]; pagination: PaginationMetadata }>(
      `/api/admin/dashboard/error-requests?page=${encodeURIComponent(String(page))}&limit=20`
    );
    state.todayErrorRequests = data.requestLogs || [];
    state.todayErrorRequestsPagination = data.pagination || {
      page,
      pageSize: 20,
      total: state.todayErrorRequests.length,
      totalPages: 1
    };
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    state.todayErrorRequestsLoading = false;
    renderTodayErrorRequests();
  }
  els.todayErrorRequests?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function closeTodayErrorRequests(): void {
  state.todayErrorRequestsOpen = false;
  renderTodayErrorRequests();
}

async function loadAdminRevenueSummary(): Promise<void> {
  const data = await api<{ revenue: RechargeRevenueSummary }>("/api/admin/dashboard/revenue");
  state.adminRechargeRevenue = data.revenue;
}

async function loadAdminWalletTransactions(): Promise<void> {
  state.adminWalletTransactionsLoading = true;
  try {
    const data = await api<{ transactions: WalletTransaction[]; summary: WalletTransactionSummary }>(
      `/api/admin/wallet-transactions?${buildWalletTransactionQuery(state.adminWalletFilters, 100)}`
    );
    state.adminWalletTransactions = data.transactions || [];
    state.adminWalletTransactionSummary = data.summary || null;
    state.adminWalletTransactionsLoaded = true;
  } finally {
    state.adminWalletTransactionsLoading = false;
  }
}

async function loadAdminProductsData(): Promise<void> {
  const [plansResult, billingRatesResult, modelsResult] = await Promise.allSettled([
    api<{ plans: PricingPlan[] }>("/api/admin/pricing-plans"),
    api<BillingModelRates>("/api/admin/billing/model-rates"),
    api<{ models: PublicModel[]; modelManagement?: PublicModelManagementStatus }>("/api/admin/models")
  ]);
  applySettledResult("admin pricing plans", plansResult, (data) => {
    state.pricingPlans = data.plans || [];
  });
  applySettledResult("admin billing rates", billingRatesResult, (data) => {
    state.billingModelRates = data;
  });
  applySettledResult("admin models for products", modelsResult, (data) => {
    state.publicModels = data.models || [];
    state.publicModelManagement = data.modelManagement || state.publicModelManagement;
  });
}

async function loadAdminOrdersData(): Promise<void> {
  const data = await api<{ orders: Order[] }>("/api/admin/orders?limit=100");
  state.adminOrders = data.orders || [];
}

async function loadAdminEntitlementsData(): Promise<void> {
  const [data, operations] = await Promise.all([
    api<{ entitlements: MemberEntitlement[] }>("/api/admin/entitlements?limit=100"),
    api<MonthlyPassOperations>("/api/admin/billing/monthly-pass-operations?limit=50")
  ]);
  state.adminEntitlements = data.entitlements || [];
  state.adminMonthlyPassOperations = operations;
}

async function loadAdminSecondaryData(): Promise<void> {
  await Promise.allSettled([
    loadAdminGptData(),
    loadAdminHealthData()
  ]);
}

async function loadAdminHealthData(): Promise<void> {
  const [healthResult, oauthResult] = await Promise.allSettled([
    api<HealthStatus>("/api/admin/health"),
    api<OauthStatus>("/api/admin/oauth-status")
  ]);

  applySettledResult("admin health", healthResult, (data) => {
    state.adminHealth = data;
  });
  applySettledResult("admin oauth", oauthResult, (data) => {
    state.adminOauth = data;
  });

  renderAdminHealth();
}

async function loadAdminGptData(): Promise<void> {
  const [modelsResult, accountsResult, failoverEventsResult] = await Promise.allSettled([
    api<{ models: PublicModel[]; modelManagement?: PublicModelManagementStatus }>("/api/admin/models"),
    api<{ accounts: ChatGptAccount[] }>("/api/admin/accounts"),
    api<{ events: AccountFailoverEvent[] }>("/api/admin/account-failover-events?limit=80")
  ]);

  applySettledResult("admin models", modelsResult, (data) => {
    state.publicModels = data.models || [];
    state.publicModelManagement = data.modelManagement || null;
  });
  applySettledResult("admin accounts", accountsResult, (data) => {
    state.chatGptAccounts = data.accounts || [];
  });
  applySettledResult("admin failover events", failoverEventsResult, (data) => {
    state.chatGptFailoverEvents = data.events || [];
  });

  renderAdminGpt();
}

export async function refreshAdminCurrentView(): Promise<void> {
  switch (state.currentView) {
    case "team":
      await Promise.all([
        loadAll(),
        loadAdminRequestLogs(),
        loadAdminRevenueSummary(),
        state.todayErrorRequestsOpen
          ? openTodayErrorRequests(state.todayErrorRequestsPagination.page)
          : Promise.resolve()
      ]);
      renderAdminPage();
      return;
    case "users":
    case "userDetail":
      await Promise.all([loadAdminUsersData(), loadAdminChannelsData()]);
      renderAdminUsers();
      return;
    case "channels":
      await loadAdminChannelsData();
      renderAdminChannels();
      return;
    case "wallets":
      await loadAdminWalletTransactions();
      renderAdminWallets();
      return;
    case "products":
      await loadAdminProductsData();
      renderAdminProducts();
      return;
    case "orders":
      await loadAdminOrdersData();
      renderAdminOrders();
      return;
    case "entitlements":
      await loadAdminEntitlementsData();
      renderAdminEntitlements();
      return;
    case "gpt":
      await loadAdminGptData();
      renderAdminGpt();
      return;
    case "health":
      await loadAdminHealthData();
      renderAdminHealth();
      return;
    case "docs":
      renderAdminDocs();
      return;
    default:
      return;
  }
}

function applySettledResult<T>(
  label: string,
  result: PromiseSettledResult<T>,
  apply: (value: T) => void
): void {
  if (result.status === "fulfilled") {
    apply(result.value);
    return;
  }
  console.error(`${label} load failed:`, result.reason);
}

function buildWalletTransactionQuery(
  filters: { memberId?: string; from?: string; to?: string; type?: string },
  limit = 100
): string {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (filters.memberId) params.set("memberId", filters.memberId);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.type) params.set("type", filters.type);
  return params.toString();
}

function triggerDownload(path: string): void {
  const link = document.createElement("a");
  link.href = path;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function formatMoney(amount: number | null | undefined, currency = "CNY"): string {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) return "-";
  const prefix = currency === "CNY" ? "¥" : `${currency} `;
  return `${prefix}${Number(amount).toFixed(2)}`;
}

function formatWalletCredits(value: number | null | undefined): string {
  return formatMicrocreditsAsCredits(value);
}

function walletValueTitle(value: number | null | undefined): string {
  return `原始存储值: ${fmtInt(value)} microcredits`;
}

function activePaidRechargePlans(): PricingPlan[] {
  return (state.pricingPlans || []).filter((plan) =>
    Number(plan.isActive) === 1 &&
    Number(plan.price) > 0 &&
    Number(plan.faceAmount ?? plan.price) > 0 &&
    Number(plan.tokenAmount) > 0
  );
}

function calculatePlanTopup(plan: PricingPlan, cashAmount: number): { points: number; faceAmount: number } {
  const price = Number(plan.price);
  const planFaceAmount = Number(plan.faceAmount ?? plan.price);
  const faceAmount = cashAmount * planFaceAmount / price;
  const points = Math.floor((faceAmount * Number(plan.tokenAmount) / planFaceAmount) + 1e-9);
  return { points, faceAmount };
}

function updateRechargePointsPreview(): void {
  const planInput = document.querySelector<HTMLSelectElement>("#recharge-user-plan");
  const cashInput = document.querySelector<HTMLInputElement>("#recharge-user-cash");
  const preview = document.querySelector<HTMLElement>("#recharge-user-points-preview");
  if (!preview) return;
  const plan = activePaidRechargePlans().find((item) => item.id === planInput?.value);
  const cashAmount = Number(cashInput?.value || 0);
  if (!plan || !Number.isFinite(cashAmount) || cashAmount <= 0) {
    preview.textContent = "-";
    return;
  }
  const topup = calculatePlanTopup(plan, cashAmount);
  preview.textContent = topup.points > 0
    ? `${fmtNum(topup.points)} 点 · 票面 ${formatMoney(topup.faceAmount, plan.currency)}`
    : "-";
}

// ===== Admin 渲染 =====

const adminUserDetailTabs: AdminUserDetailTab[] = ["usage", "policy-limits", "keys"];

function readPositiveInteger(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function applyAdminListParams(params: URLSearchParams): void {
  state.adminUsersSearch = params.get("q") || "";
  state.pagination.users.page = readPositiveInteger(params.get("page"), 1);
  state.pagination.users.pageSize = readPositiveInteger(params.get("pageSize"), 10);
}

function adminListSearchParams(view: AdminView = "users"): URLSearchParams {
  const params = new URLSearchParams();
  if (view !== "team" && view !== "userDetail") params.set("view", view);
  if (view === "users" || view === "userDetail") {
    if (state.adminUsersSearch) params.set("q", state.adminUsersSearch);
    if (state.pagination.users.page > 1) params.set("page", String(state.pagination.users.page));
    if (state.pagination.users.pageSize !== 10) params.set("pageSize", String(state.pagination.users.pageSize));
  }
  return params;
}

function adminListUrl(view: AdminView = "users"): string {
  const params = adminListSearchParams(view);
  const query = params.toString();
  return `/console/admin/${query ? `?${query}` : ""}`;
}

function adminUserDetailUrl(userId: string, tab: AdminUserDetailTab): string {
  const params = adminListSearchParams("userDetail");
  if (tab !== "usage") params.set("tab", tab);
  const query = params.toString();
  return `/console/admin/users/${encodeURIComponent(userId)}${query ? `?${query}` : ""}`;
}

export function initializeAdminRouteFromLocation(): void {
  const params = new URLSearchParams(window.location.search);
  applyAdminListParams(params);
  const match = window.location.pathname.replace(/\/+$/, "").match(/^\/console\/admin\/users\/([^/]+)$/);
  if (match) {
    let userId = match[1];
    try { userId = decodeURIComponent(userId); } catch { /* keep raw path segment */ }
    const tab = params.get("tab") as AdminUserDetailTab | null;
    state.currentView = "userDetail";
    state.adminSelectedUserId = userId;
    state.adminUserDetailTab = tab && adminUserDetailTabs.includes(tab) ? tab : "usage";
    return;
  }
  const requested = params.get("view") as AdminView | null;
  const allowed: AdminView[] = ["team", "users", "channels", "wallets", "products", "orders", "entitlements", "gpt", "health", "docs"];
  state.currentView = requested && allowed.includes(requested) ? requested : "team";
  state.adminSelectedUserId = null;
  state.adminUserDetailTab = "usage";
  state.adminKeyDrawer = null;
}

export function navigateAdminView(view: AdminView): void {
  const target = view === "userDetail" ? "users" : view;
  state.currentView = target;
  state.adminSelectedUserId = null;
  state.adminKeyDrawer = null;
  window.history.pushState({}, "", adminListUrl(target));
  renderAdminPage();
}

export function openAdminUserDetail(userId: string): void {
  if (!findAdminUser(userId)) return;
  state.currentView = "userDetail";
  state.adminSelectedUserId = userId;
  state.adminUserDetailTab = "usage";
  state.adminKeyDrawer = null;
  window.history.pushState({}, "", adminUserDetailUrl(userId, "usage"));
  renderAdminPage();
}

export function returnToAdminUsers(): void {
  state.currentView = "users";
  state.adminSelectedUserId = null;
  state.adminKeyDrawer = null;
  window.history.pushState({}, "", adminListUrl("users"));
  renderAdminPage();
}

export function setAdminUserDetailTab(tab: string): void {
  if (!adminUserDetailTabs.includes(tab as AdminUserDetailTab) || !state.adminSelectedUserId) return;
  state.adminUserDetailTab = tab as AdminUserDetailTab;
  state.adminKeyDrawer = null;
  window.history.replaceState({}, "", adminUserDetailUrl(state.adminSelectedUserId, state.adminUserDetailTab));
  renderAdminUsers();
}

/** 渲染管理后台主页面 */
export function renderAdminPage(): void {
  const roles = parseUserRoles(state.user);
  if (!roles.includes("admin")) return;

  renderView();
  renderTeamOverview();
  renderTodayErrorRequests();
  renderRequestLogs();
  renderAdminUsers();
  renderAdminChannels();
  renderAdminWallets();
  renderAdminProducts();
  renderAdminOrders();
  renderAdminEntitlements();
  renderAdminGpt();
  renderAdminHealth();
  renderAdminDocs();
}

/** 切换 admin 子视图(侧边栏导航) */
export function renderView(): void {
  const labels: Record<AdminView, [string, string]> = {
    team: ["Dashboard", "系统看板"],
    users: ["Users", "用户管理"],
    userDetail: ["User detail", "用户详情"],
    channels: ["Channels", "通道管理"],
    wallets: ["Wallet", "钱包履历"],
    products: ["Products", "产品管理"],
    orders: ["Orders", "订单管理"],
    entitlements: ["Monthly Pass", "月卡权益"],
    gpt: ["GPT", "GPT 账号"],
    health: ["Health", "系统状态"],
    docs: ["Docs", "使用文档"]
  };

  els.navItems.forEach((item) => {
    const activeView = state.currentView === "userDetail" ? "users" : state.currentView;
    item.classList.toggle("active", item.dataset.view === activeView);
  });

  els.views.forEach((view) => {
    view.classList.toggle("active", view.id === `view-${state.currentView}`);
  });

  const [eyebrow, title] = labels[state.currentView] || labels.team;
  if (els.pageEyebrow) els.pageEyebrow.textContent = eyebrow;
  if (els.pageTitle) els.pageTitle.textContent = title;

  const stats = [
    `<span class="pill">${state.members.length} 用户</span>`
  ];
  if (state.currentView === "docs") {
    stats.push(`<span class="pill">管理员手册</span>`);
    stats.push(`<span class="pill">用户手册</span>`);
  }
  if (els.pageMeta) els.pageMeta.innerHTML = stats.join("");
}

function channelOptions(selectedId: string | null | undefined, inheritanceLabel: string): string {
  const options = [`<option value="">${escapeHtml(inheritanceLabel)}</option>`];
  for (const channel of state.adminChannels) {
    const disabled = channel.status !== "active" ? "disabled" : "";
    options.push(`
      <option value="${escapeHtml(channel.id)}" ${selectedId === channel.id ? "selected" : ""} ${disabled}>
        ${escapeHtml(channel.name)}${channel.isDefault ? "（默认）" : ""}${channel.status !== "active" ? ` · ${escapeHtml(channel.status)}` : ""}
      </option>
    `);
  }
  return options.join("");
}

export function renderAdminChannels(): void {
  if (!els.adminChannelsContent || state.currentView !== "channels") return;
  const visibleChannels = state.adminChannels.filter((channel) => channel.metadata?.archived !== true);
  const providerInstanceById = new Map(state.adminProviderInstances.map((instance) => [instance.id, instance]));
  const instanceOptions = (selectedId: string | null) => `
    <option value="">未绑定</option>
    ${state.adminProviderInstances.map((instance) => `
      <option value="${escapeHtml(instance.id)}" ${instance.id === selectedId ? "selected" : ""}>
        ${escapeHtml(instance.name)} · ${escapeHtml(instance.status)}
      </option>
    `).join("")}
  `;
  const topologyMarkup = `
    <section class="routing-topology" aria-label="通道、服务实例与执行节点关系">
      <div class="routing-topology-head">
        <div><p class="eyebrow">ROUTING MAP</p><h3>一条请求如何落到执行节点</h3><p class="muted">通道决定入口和模型目录，服务实例承载调度边界，节点才是实际执行进程或外部转发地址。</p></div>
        <div class="routing-topology-legend"><span><i class="topology-dot topology-dot-channel"></i>通道</span><span><i class="topology-dot topology-dot-instance"></i>服务实例</span><span><i class="topology-dot topology-dot-node"></i>执行节点</span></div>
      </div>
      <div class="routing-topology-grid">
        ${visibleChannels.map((channel) => {
          const providerInstance = channel.providerInstanceId ? providerInstanceById.get(channel.providerInstanceId) : null;
          const nodeCount = providerInstance?.nodes.length || 0;
          const accountCount = providerInstance?.nodes.filter((node) => node.mode === "systemd_instance").length || 0;
          const forwarderCount = providerInstance?.nodes.filter((node) => node.mode === "external").length || 0;
          return `<article class="routing-topology-card">
            <div class="routing-topology-card-head"><span class="topology-kind topology-kind-channel">通道</span>${pill(channel.status)}${channel.isDefault ? '<span class="pill">默认</span>' : ""}</div>
            <div class="routing-topology-flow">
              <div class="topology-stage"><span>入口通道</span><strong>${escapeHtml(channel.name)}</strong><small><code>${escapeHtml(channel.slug)}</code></small></div>
              <span class="topology-arrow" aria-hidden="true">→</span>
              <div class="topology-stage"><span>服务实例</span><strong>${escapeHtml(providerInstance?.name || "未绑定实例")}</strong><small>${providerInstance ? `${nodeCount} 个执行节点` : "新请求暂不可用"}</small></div>
              <span class="topology-arrow" aria-hidden="true">→</span>
              <div class="topology-stage"><span>执行节点</span><strong>${nodeCount ? `${nodeCount} 个节点` : "暂无节点"}</strong><small>${nodeCount ? `${accountCount} 账号 · ${forwarderCount} 转发` : "在服务实例中添加节点"}</small></div>
            </div>
          </article>`;
        }).join("") || '<div class="routing-topology-empty">暂无可展示的通道关系。</div>'}
      </div>
    </section>
  `;
  const channelTab = state.adminChannelTab;
  els.adminChannelsContent.innerHTML = `
    <section class="panel panel-fill channel-management-shell">
      <div class="panel-heading">
        <div><p class="eyebrow">Routing</p><h2>通道、服务实例与节点</h2><p class="muted">通道只绑定服务实例；一个服务实例可以包含多个节点。账号节点由独立 Bridge 进程承载，转发节点直连外部兼容接口。</p></div>
        <button class="secondary-button" data-action="refresh-admin">刷新</button>
      </div>
      <nav class="management-tabs" aria-label="通道管理分类">
        <button class="${channelTab === "channels" ? "active" : ""}" data-action="set-admin-channel-tab" data-tab="channels">通道 <span>${state.adminChannels.length}</span></button>
        <button class="${channelTab === "instances" ? "active" : ""}" data-action="set-admin-channel-tab" data-tab="instances">服务实例 <span>${state.adminProviderInstances.length}</span></button>
      </nav>
      ${topologyMarkup}
      ${channelTab === "channels" ? `
        <div class="management-toolbar"><div><strong>通道</strong><small>切换绑定只影响新请求，已有会话继续固定原账号节点。</small></div><button class="primary-button" data-action="create-channel">新增通道</button></div>
        <div class="table-wrap"><table class="sub-table management-table channel-table">
          <thead><tr><th>通道</th><th>状态</th><th>绑定服务实例</th><th>公开模型目录</th><th>运行情况</th><th>今日 Token</th><th>本周 Token</th><th>累计 Token</th><th>通道并发</th><th>用户与 Key</th><th>操作</th></tr></thead>
          <tbody>${state.adminChannels.map((channel) => `
            <tr>
              <td><strong>${escapeHtml(channel.name)}</strong>${channel.isDefault ? ' <span class="pill">默认</span>' : ""}<small><code>${escapeHtml(channel.slug)}</code></small></td>
              <td><select id="channelStatus-${escapeHtml(channel.id)}" aria-label="通道状态">${["active", "draining", "disabled"].map((status) => `<option value="${status}" ${channel.status === status ? "selected" : ""}>${status}</option>`).join("")}</select></td>
              <td><select id="channelInstance-${escapeHtml(channel.id)}" aria-label="绑定服务实例">${instanceOptions(channel.providerInstanceId)}</select><small>直连执行</small></td>
              <td><input id="channelModels-${escapeHtml(channel.id)}" value="${escapeHtml((channel.allowedModels || []).join(", "))}" placeholder="留空使用公共目录" aria-label="公开模型目录"><small>逗号或换行分隔；直连通道优先使用此目录</small></td>
              <td>${channel.providerInstanceId ? `${pill(channel.providerInstanceStatus || "disabled")}<small>${escapeHtml(channel.providerInstanceName || channel.providerInstanceId)}</small>` : '<span class="pill pill-warning">未绑定</span><small>新请求将不可用</small>'}</td>
              <td class="management-token-cell"><strong>${fmtNum(channel.tokenUsage?.todayTokens || 0)}</strong></td>
              <td class="management-token-cell"><strong>${fmtNum(channel.tokenUsage?.weekTokens || 0)}</strong></td>
              <td class="management-token-cell"><strong>${fmtNum(channel.tokenUsage?.totalTokens || 0)}</strong></td>
              <td><input id="channelConcurrency-${escapeHtml(channel.id)}" type="number" min="1" step="1" value="${channel.maxConcurrency ?? ""}" placeholder="不限"><small>${channel.activeRequests || 0} 个处理中</small></td>
              <td class="channel-consumer-cell"><strong>使用过：${channel.tokenUsage?.usedMemberCount || 0} 用户 · ${channel.tokenUsage?.usedApiKeyCount || 0} Key</strong><small>当前绑定：${channel.assignedMemberCount || 0} 用户 · ${channel.assignedApiKeyCount || 0} Key</small></td>
              <td><div class="table-action-stack"><button class="secondary-button compact-button" data-action="save-channel" data-id="${escapeHtml(channel.id)}">保存</button>${channel.isDefault ? "" : `<button class="ghost-button compact-button" data-action="set-default-channel" data-id="${escapeHtml(channel.id)}">设为默认</button>`}${channel.isDefault ? "" : `<button class="ghost-button danger-button compact-button" data-action="delete-channel" data-id="${escapeHtml(channel.id)}" data-name="${escapeHtml(channel.name)}" ${channel.status === "disabled" ? "" : "disabled title=\"请先停用通道\""}>删除</button>`}</div></td>
            </tr>
          `).join("") || '<tr><td colspan="11" class="muted">暂无通道。</td></tr>'}</tbody>
        </table></div>
        <details class="channel-override-audit" open><summary>临时通道切换（${(state.adminChannelOverrides || []).filter((item) => !item.revokedAt && new Date(item.expiresAt) > new Date()).length} 个生效中）</summary><div class="table-wrap"><table class="sub-table"><thead><tr><th>对象</th><th>目标通道</th><th>到期</th><th>原因</th><th>操作人</th><th>操作</th></tr></thead><tbody>${(state.adminChannelOverrides || []).map((override) => `<tr><td><code>${escapeHtml(override.subjectType === "api_key" ? override.apiKeyId || "API Key" : override.memberId)}</code><small>${override.subjectType === "api_key" ? "API Key" : "用户"}</small></td><td>${escapeHtml(override.channelName || override.channelId)}</td><td>${escapeHtml(formatTime(override.expiresAt))}</td><td>${escapeHtml(override.reason)}</td><td><code>${escapeHtml(override.requestedByMemberId)}</code></td><td>${!override.revokedAt && new Date(override.expiresAt) > new Date() ? `<button class="ghost-button compact-button" data-action="revoke-channel-override" data-id="${escapeHtml(override.id)}">立即撤销</button>` : `<span class="muted">${override.revokedAt ? "已撤销" : "已到期"}</span>`}</td></tr>`).join("") || '<tr><td colspan="6" class="muted">暂无临时切换记录。</td></tr>'}</tbody></table></div></details>
      ` : `
        <div class="management-toolbar"><div><strong>服务实例</strong><small>一个服务实例可以包含多个节点；通道只绑定服务实例，不直接绑定账号、外部接口或进程。</small></div><button class="secondary-button" data-action="create-provider-instance">新增服务实例</button></div>
        <div class="table-wrap"><table class="sub-table management-table instance-table">
          <thead><tr><th>服务实例</th><th>状态</th><th>节点</th><th>今日 Token</th><th>本周 Token</th><th>累计 Token</th><th>实例并发</th><th>引用通道</th><th>操作</th></tr></thead>
          <tbody>${state.adminProviderInstances.map((instance) => `
            <tr>
              <td><strong>${escapeHtml(instance.name)}</strong><small><code>${escapeHtml(instance.slug)}</code></small></td>
              <td><select id="providerStatus-${escapeHtml(instance.id)}">${["active", "draining", "disabled"].map((status) => `<option value="${status}" ${instance.status === status ? "selected" : ""}>${status}</option>`).join("")}</select></td>
              <td><strong>${instance.nodes.length} 个</strong><small>${instance.nodes.filter((node) => node.mode === "systemd_instance").length} 个账号节点 · ${instance.nodes.filter((node) => node.mode === "external").length} 个转发节点</small></td>
              <td class="management-token-cell"><strong>${fmtNum(instance.tokenUsage?.todayTokens || 0)}</strong></td>
              <td class="management-token-cell"><strong>${fmtNum(instance.tokenUsage?.weekTokens || 0)}</strong></td>
              <td class="management-token-cell"><strong>${fmtNum(instance.tokenUsage?.totalTokens || 0)}</strong></td>
              <td><input id="providerConcurrency-${escapeHtml(instance.id)}" type="number" min="1" value="${instance.maxConcurrency ?? ""}" placeholder="不限"><small>${instance.activeRequests}/${instance.maxConcurrency ?? "∞"} 处理中</small></td>
              <td><div class="channel-reference-list">${instance.channels?.length ? instance.channels.map((channel) => `<span class="pill">${escapeHtml(channel.name)}${channel.isDefault ? " · 默认" : ""}</span>`).join("") : '<span class="muted">暂未引用</span>'}</div></td>
              <td><div class="table-action-stack"><button class="secondary-button compact-button" data-action="save-provider-instance" data-id="${escapeHtml(instance.id)}">保存</button><button class="ghost-button compact-button" data-action="delete-provider-instance" data-id="${escapeHtml(instance.id)}" data-name="${escapeHtml(instance.name)}">删除</button></div></td>
            </tr>
          `).join("") || '<tr><td colspan="9" class="muted">暂无服务实例。</td></tr>'}</tbody>
        </table></div>
        <div class="management-toolbar"><div><strong>节点列表（Bridge / 转发）</strong><small>账号节点 = 独立 chatgpt-bridge 进程 + 固定 GPT 账号；转发节点 = 外部 OpenAI 兼容上游 + 外部 Key。</small></div><div class="table-action-stack"><button class="secondary-button" data-action="create-forwarder-instance">新增转发节点</button><button class="primary-button" data-action="create-bridge-instance">新增 Bridge 账号节点</button></div></div>
        <div class="table-wrap"><table class="sub-table management-table instance-table">
          <thead><tr><th>节点</th><th>所属服务实例</th><th>状态</th><th>节点类型与凭据</th><th>执行路径</th><th>服务地址</th><th>今日 Token</th><th>本周 Token</th><th>累计 Token</th><th>节点并发</th><th>健康</th><th>操作</th></tr></thead>
          <tbody>${state.adminBridgeInstances.map((instance) => `
            <tr>
              <td><strong>${escapeHtml(instance.name)}</strong><small><code>${escapeHtml(instance.runtimeKey)}</code></small></td>
              <td><strong>${escapeHtml(instance.providerInstanceName || instance.providerInstanceId)}</strong></td>
              <td>${pill(instance.status)}</td>
              <td>${instance.upstreamAuthConfigured ? `<strong>外部转发节点 · Key</strong><small>外部密钥已配置</small>` : instance.accountId ? `<strong>Bridge 账号节点 · ${escapeHtml(instance.accountLabel || instance.accountId)}</strong><small>独立进程 · 账号固定</small>` : `<select id="bridgeAccount-${escapeHtml(instance.id)}"><option value="">请选择 GPT 账号</option>${state.chatGptAccounts.map((account) => `<option value="${escapeHtml(account.id)}">${escapeHtml(account.label)}</option>`).join("")}</select>`}</td>
              <td>${instance.upstreamAuthConfigured ? '<strong>外部直连</strong><small>转发节点固定访问外部上游</small>' : '<strong>Bridge 直连</strong><small>独立 Bridge 进程；继承 Channel 策略</small>'}</td>
              <td><code>${escapeHtml(instance.baseUrl)}</code><small>${escapeHtml(instance.serviceName)}</small></td>
              <td class="management-token-cell"><strong>${fmtNum(instance.tokenUsage?.todayTokens || 0)}</strong></td>
              <td class="management-token-cell"><strong>${fmtNum(instance.tokenUsage?.weekTokens || 0)}</strong></td>
              <td class="management-token-cell"><strong>${fmtNum(instance.tokenUsage?.totalTokens || 0)}</strong></td>
              <td><input id="bridgeConcurrency-${escapeHtml(instance.id)}" type="number" min="1" value="${instance.maxConcurrency ?? ""}" placeholder="不限"><small>${instance.activeRequests}/${instance.maxConcurrency ?? "∞"} 处理中</small></td>
              <td><strong>${instance.lastHealthAt ? escapeHtml(formatTime(instance.lastHealthAt)) : "未探测"}</strong><small>${instance.failureCount ? `失败 ${instance.failureCount}` : "无近期故障"}</small></td>
              <td><div class="table-action-stack"><button class="secondary-button compact-button" data-action="save-bridge-instance" data-id="${escapeHtml(instance.id)}">保存</button>${instance.upstreamAuthConfigured ? "" : instance.mode === "systemd_instance" ? `<button class="ghost-button compact-button" data-action="bridge-action" data-id="${escapeHtml(instance.id)}" data-operation="start">启动</button>` : ""}${instance.upstreamAuthConfigured ? "" : `<button class="ghost-button compact-button" data-action="bridge-action" data-id="${escapeHtml(instance.id)}" data-operation="restart">重启</button>`}${instance.upstreamAuthConfigured ? "" : instance.mode === "systemd_instance" ? `<button class="ghost-button compact-button" data-action="bridge-action" data-id="${escapeHtml(instance.id)}" data-operation="stop">停止</button>` : ""}<button class="ghost-button compact-button" data-action="bridge-action" data-id="${escapeHtml(instance.id)}" data-operation="probe">探测</button></div></td>
            </tr>
          `).join("") || '<tr><td colspan="12" class="muted">暂无账号执行节点。</td></tr>'}</tbody>
        </table></div>
        <details class="management-audit"><summary>实例事件记录（${state.adminBridgeFailoverEvents.length}）</summary><div class="table-wrap"><table class="sub-table"><thead><tr><th>时间</th><th>入口</th><th>通道</th><th>实例</th><th>状态</th><th>原因</th></tr></thead><tbody>${state.adminBridgeFailoverEvents.map((event) => `<tr><td>${escapeHtml(formatTime(event.createdAt))}</td><td>${escapeHtml(event.route)}</td><td><code>${escapeHtml(event.channelId)}</code></td><td><code>${escapeHtml(event.fromInstanceId || "-")}</code></td><td>${escapeHtml(event.status)}</td><td>${escapeHtml(event.errorMessage || event.trigger)}</td></tr>`).join("") || '<tr><td colspan="6" class="muted">暂无事件。</td></tr>'}</tbody></table></div></details>
      `}
    </section>
  `;
}

export function setAdminChannelTab(tab: "channels" | "instances"): void {
  state.adminChannelTab = tab;
  renderAdminChannels();
}

function showManagementEditor({ title, description, body, onSave }: { title: string; description: string; body: string; onSave: (dialog: HTMLDialogElement) => Promise<void> }): void {
  const dialog = document.createElement("dialog");
  dialog.className = "management-editor-dialog";
  dialog.innerHTML = `<form class="management-editor-form"><header><div><p class="eyebrow">Management</p><h2>${escapeHtml(title)}</h2><p>${escapeHtml(description)}</p></div><button type="button" class="icon-button" data-editor-cancel>×</button></header><div class="management-editor-body">${body}</div><footer><button type="button" class="secondary-button" data-editor-cancel>取消</button><button type="submit" class="primary-button">保存</button></footer></form>`;
  dialog.querySelectorAll<HTMLElement>("[data-editor-cancel]").forEach((button) => button.addEventListener("click", () => dialog.close()));
  dialog.querySelector("form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    try {
      await onSave(dialog);
      dialog.close();
    } catch (error) {
      toast((error as Error).message, "error");
    }
  });
  dialog.addEventListener("close", () => dialog.remove(), { once: true });
  document.body.append(dialog);
  dialog.showModal();
}

export async function createBridgeInstance(): Promise<void> {
  showManagementEditor({
    title: "新增 Bridge 账号节点",
    description: "节点属于一个服务实例，固定使用一个 GPT 账号和独立 chatgpt-bridge 进程。",
    body: `<div class="form-grid"><label><span>所属服务实例</span><select id="bridgeCreateProvider"><option value="">请选择</option>${state.adminProviderInstances.map((instance) => `<option value="${escapeHtml(instance.id)}">${escapeHtml(instance.name)}</option>`).join("")}</select></label><label><span>绑定账号</span><select id="bridgeCreateAccount"><option value="">请选择</option>${state.chatGptAccounts.map((account) => `<option value="${escapeHtml(account.id)}">${escapeHtml(account.label)}</option>`).join("")}</select></label><label><span>节点名称</span><input id="bridgeCreateName" type="text" placeholder="例如：jerry 账号节点"></label><label><span>运行标识</span><input id="bridgeCreateRuntimeKey" type="text" placeholder="例如：jerry-01"></label><label><span>监听端口</span><input id="bridgeCreatePort" type="number" min="1024" max="65535" placeholder="例如：10532"></label><label><span>节点并发（可空）</span><input id="bridgeCreateMaxConcurrency" type="number" min="1" placeholder="不限制"></label></div>`,
    onSave: async (dialog) => {
      const providerInstanceId = dialog.querySelector<HTMLSelectElement>("#bridgeCreateProvider")?.value || "";
      const accountId = dialog.querySelector<HTMLSelectElement>("#bridgeCreateAccount")?.value || "";
      const name = dialog.querySelector<HTMLInputElement>("#bridgeCreateName")?.value.trim() || "";
      const runtimeKey = dialog.querySelector<HTMLInputElement>("#bridgeCreateRuntimeKey")?.value.trim() || "";
      const port = Number(dialog.querySelector<HTMLInputElement>("#bridgeCreatePort")?.value || 0);
      const maxConcurrencyValue = dialog.querySelector<HTMLInputElement>("#bridgeCreateMaxConcurrency")?.value || "";
      await api("/api/admin/bridge-instances", { method: "POST", body: JSON.stringify({ providerInstanceId, accountId, name, runtimeKey, port, maxConcurrency: maxConcurrencyValue ? Number(maxConcurrencyValue) : null }) });
      toast("账号执行节点已创建。", "success");
      await loadAdminChannelsData();
      renderAdminChannels();
    }
  });
}

export async function createProviderInstance(): Promise<void> {
  showManagementEditor({
    title: "新增服务实例",
    description: "通道只绑定服务实例；账号、进程和故障隔离由实例内部负责。",
    body: `<div class="form-grid"><label><span>实例名称</span><input id="providerCreateName" type="text" placeholder="例如：GPT 主服务"></label><label><span>内部标识</span><input id="providerCreateSlug" type="text" placeholder="例如：gpt-main"></label><label><span>实例并发（可空）</span><input id="providerCreateConcurrency" type="number" min="1" placeholder="不限制"></label></div>`,
    onSave: async (dialog) => {
      const name = dialog.querySelector<HTMLInputElement>("#providerCreateName")?.value.trim() || "";
      const slug = dialog.querySelector<HTMLInputElement>("#providerCreateSlug")?.value.trim() || "";
      const concurrency = dialog.querySelector<HTMLInputElement>("#providerCreateConcurrency")?.value.trim() || "";
      await api("/api/admin/provider-instances", {
        method: "POST",
        body: JSON.stringify({ name, slug, maxConcurrency: concurrency ? Number(concurrency) : null })
      });
      toast("服务实例已创建，现在可以向其中添加账号节点。", "success");
      await loadAdminChannelsData();
      renderAdminChannels();
    }
  });
}

export async function createForwarderInstance(): Promise<void> {
  showManagementEditor({
    title: "新增转发节点",
    description: "转发节点必须属于一个服务实例；通道仍然只绑定这个服务实例。",
    body: `<div class="form-grid"><label><span>所属服务实例</span><select id="forwarderCreateProvider"><option value="">请选择</option>${state.adminProviderInstances.map((instance) => `<option value="${escapeHtml(instance.id)}">${escapeHtml(instance.name)}</option>`).join("")}</select></label><label><span>节点名称</span><input id="forwarderCreateName" type="text" placeholder="例如：旁路接口"></label><label><span>运行标识</span><input id="forwarderCreateRuntimeKey" type="text" placeholder="例如：bypass-01"></label><label><span>Base URL</span><input id="forwarderCreateBaseUrl" type="url" placeholder="https://example.com/v1"></label><label><span>上游协议</span><select id="forwarderCreateProtocol"><option value="responses">Responses API</option><option value="chat_completions">Chat Completions（OpenCode 免费模型）</option></select></label><label><span>上游 Key</span><input id="forwarderCreateKey" type="password" autocomplete="new-password" placeholder="sk-..."></label><label><span>节点并发（可空）</span><input id="forwarderCreateConcurrency" type="number" min="1" placeholder="不限制"></label></div>`,
    onSave: async (dialog) => {
      const providerInstanceId = dialog.querySelector<HTMLSelectElement>("#forwarderCreateProvider")?.value || "";
      const name = dialog.querySelector<HTMLInputElement>("#forwarderCreateName")?.value.trim() || "";
      const runtimeKey = dialog.querySelector<HTMLInputElement>("#forwarderCreateRuntimeKey")?.value.trim() || "";
      const baseUrl = dialog.querySelector<HTMLInputElement>("#forwarderCreateBaseUrl")?.value.trim() || "";
      const upstreamProtocol = dialog.querySelector<HTMLSelectElement>("#forwarderCreateProtocol")?.value || "responses";
      const upstreamApiKey = dialog.querySelector<HTMLInputElement>("#forwarderCreateKey")?.value.trim() || "";
      const concurrency = dialog.querySelector<HTMLInputElement>("#forwarderCreateConcurrency")?.value.trim() || "";
      await api("/api/admin/bridge-instances", {
        method: "POST",
        body: JSON.stringify({
          providerInstanceId,
          name,
          runtimeKey,
          mode: "external",
          routingMode: "passthrough",
          baseUrl,
          upstreamApiKey,
          upstreamProtocol,
          maxConcurrency: concurrency ? Number(concurrency) : null
        })
      });
      toast("转发节点已创建。", "success");
      await loadAdminChannelsData();
      renderAdminChannels();
    }
  });
}

export async function saveProviderInstance(instanceId: string): Promise<void> {
  const status = document.querySelector<HTMLSelectElement>(`#providerStatus-${CSS.escape(instanceId)}`)?.value;
  const concurrency = document.querySelector<HTMLInputElement>(`#providerConcurrency-${CSS.escape(instanceId)}`)?.value.trim() || "";
  try {
    await api(`/api/admin/provider-instances/${encodeURIComponent(instanceId)}`, {
      method: "PATCH",
      body: JSON.stringify({ status, maxConcurrency: concurrency ? Number(concurrency) : null })
    });
    toast("服务实例配置已保存。", "success");
    await loadAdminChannelsData();
    renderAdminChannels();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function deleteProviderInstance(instanceId: string, instanceName: string): Promise<void> {
  if (!confirm(`确定要删除服务实例「${instanceName}」吗？\n\n删除前必须解除所有通道绑定，并停止其中运行中的账号节点。删除后实例和其成员节点不再参与路由，但历史会话与审计记录会保留。`)) return;
  try {
    await api(`/api/admin/provider-instances/${encodeURIComponent(instanceId)}`, { method: "DELETE" });
    toast("服务实例及其成员节点已删除。", "success");
    await loadAdminChannelsData();
    renderAdminChannels();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function runBridgeInstanceAction(instanceId: string, operation: string): Promise<void> {
  if (!["start", "restart", "stop", "probe"].includes(operation)) return;
  try {
    await api(`/api/admin/bridge-instances/${encodeURIComponent(instanceId)}/${operation}`, { method: "POST" });
    toast(`Bridge 操作 ${operation} 已完成。`, "success");
    await loadAdminChannelsData();
    renderAdminChannels();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function saveBridgeInstance(instanceId: string): Promise<void> {
  const accountInput = document.querySelector<HTMLSelectElement>(`#bridgeAccount-${CSS.escape(instanceId)}`);
  const maxConcurrencyValue = document.querySelector<HTMLInputElement>(`#bridgeConcurrency-${CSS.escape(instanceId)}`)?.value || "";
  try {
    const body: { accountId?: string; maxConcurrency: number | null } = {
      maxConcurrency: maxConcurrencyValue ? Number(maxConcurrencyValue) : null
    };
    if (accountInput?.value) body.accountId = accountInput.value;
    await api(`/api/admin/bridge-instances/${encodeURIComponent(instanceId)}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    });
    toast(accountInput?.value ? "账号节点已绑定，重启后生效。" : "节点配置已保存。", "success");
    await loadAdminChannelsData();
    renderAdminChannels();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function createChannel(): Promise<void> {
  showManagementEditor({
    title: "新增通道",
    description: "通道是用户和 API Key 的业务入口，只绑定一个当前实例。直连 Channel 可在此声明独立公开模型目录。",
    body: `<div class="form-grid"><label><span>通道名称</span><input id="channelCreateName" type="text" placeholder="例如：默认通道"></label><label><span>内部标识</span><input id="channelCreateSlug" type="text" placeholder="例如：default"></label><label><span>绑定服务实例</span><select id="channelCreateInstance"><option value="">暂不绑定</option>${state.adminProviderInstances.map((instance) => `<option value="${escapeHtml(instance.id)}">${escapeHtml(instance.name)} · ${escapeHtml(instance.status)}</option>`).join("")}</select></label><p class="muted">新通道固定使用直连执行。</p><label><span>公开模型目录（可空）</span><input id="channelCreateModels" type="text" placeholder="例如：codex-auto-review, gpt-5.6-terra"><small>逗号或换行分隔；留空继承当前公共目录。</small></label><label><span>通道并发（可空）</span><input id="channelCreateConcurrency" type="number" min="1" placeholder="不限制"></label></div>`,
    onSave: async (dialog) => {
      const name = dialog.querySelector<HTMLInputElement>("#channelCreateName")?.value.trim() || "";
      const slug = dialog.querySelector<HTMLInputElement>("#channelCreateSlug")?.value.trim() || "";
      const providerInstanceId = dialog.querySelector<HTMLSelectElement>("#channelCreateInstance")?.value || null;
      const concurrency = dialog.querySelector<HTMLInputElement>("#channelCreateConcurrency")?.value.trim() || "";
      const executionMode = "direct";
      const allowedModels = parseChannelModelNames(dialog.querySelector<HTMLInputElement>("#channelCreateModels")?.value);
      await api("/api/admin/channels", { method: "POST", body: JSON.stringify({ name, slug, providerInstanceId, executionMode, allowedModels, maxConcurrency: concurrency ? Number(concurrency) : null }) });
      toast("通道已创建。", "success");
      await loadAdminChannelsData();
      renderAdminChannels();
    }
  });
}

export async function saveChannel(channelId: string): Promise<void> {
  const status = document.querySelector<HTMLSelectElement>(`#channelStatus-${CSS.escape(channelId)}`)?.value;
  const providerInstanceId = document.querySelector<HTMLSelectElement>(`#channelInstance-${CSS.escape(channelId)}`)?.value || null;
  const concurrency = document.querySelector<HTMLInputElement>(`#channelConcurrency-${CSS.escape(channelId)}`)?.value.trim() || "";
  const executionMode = "direct";
  const allowedModels = parseChannelModelNames(document.querySelector<HTMLInputElement>(`#channelModels-${CSS.escape(channelId)}`)?.value);
  try {
    await api(`/api/admin/channels/${encodeURIComponent(channelId)}`, {
      method: "PATCH",
      body: JSON.stringify({ status, providerInstanceId, executionMode, allowedModels, maxConcurrency: concurrency ? Number(concurrency) : null })
    });
    toast("通道配置已保存。", "success");
    await loadAdminChannelsData();
    renderAdminChannels();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function deleteChannel(channelId: string, channelName: string): Promise<void> {
  if (!confirm(`确定要删除通道「${channelName}」吗？\n\n只有已停用且没有用户、请求或审计记录引用的通道可以删除；历史记录会被保留。`)) return;
  try {
    await api(`/api/admin/channels/${encodeURIComponent(channelId)}`, { method: "DELETE" });
    toast("通道已删除。", "success");
    await loadAdminChannelsData();
    renderAdminChannels();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

function parseChannelModelNames(value: string | undefined): string[] | null {
  const names = String(value || "")
    .split(/[\n,]/)
    .map((modelName) => modelName.trim())
    .filter(Boolean);
  return names.length ? [...new Set(names)] : null;
}

export async function setDefaultChannel(channelId: string): Promise<void> {
  try {
    await api(`/api/admin/channels/${encodeURIComponent(channelId)}`, {
      method: "PATCH",
      body: JSON.stringify({ isDefault: true })
    });
    toast("系统默认通道已更新。", "success");
    await Promise.all([loadAdminChannelsData(), loadAdminUsersData()]);
    renderAdminChannels();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function assignUserChannel(userId: string, channelId: string): Promise<void> {
  try {
    const data = await api<{ user: User }>(`/api/admin/users/${encodeURIComponent(userId)}/channel`, {
      method: "PATCH",
      body: JSON.stringify({ channelId: channelId || null })
    });
    state.adminUsers = state.adminUsers.map((user) => user.id === userId ? { ...user, ...data.user } : user);
    toast("用户通道已更新；未单独指定通道的 Key 将继承此设置。", "success");
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
    await loadAdminUsersData();
    renderAdminUsers();
  }
}

export async function assignApiKeyChannel(apiKeyId: string, channelId: string): Promise<void> {
  try {
    const data = await api<{ apiKey: ApiKey }>(`/api/admin/api-keys/${encodeURIComponent(apiKeyId)}/channel`, {
      method: "PATCH",
      body: JSON.stringify({ channelId: channelId || null })
    });
    state.adminUsers = state.adminUsers.map((user) => ({
      ...user,
      apiKeys: (user.apiKeys || []).map((key) => key.id === apiKeyId ? { ...key, ...data.apiKey } : key)
    }));
    toast("Key 通道已更新；该设置优先于用户通道。", "success");
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
    await loadAdminUsersData();
    renderAdminUsers();
  }
}

export function openTemporaryChannelOverride(subjectType: "member" | "api_key", subjectId: string, subjectLabel: string): void {
  const activeChannels = state.adminChannels.filter((channel) => channel.status === "active");
  const defaultExpiry = toDatetimeLocal(new Date(Date.now() + 60 * 60 * 1000).toISOString());
  showManagementEditor({
    title: "临时切换通道",
    description: "只影响新的网页 Chat 或 API 请求；已有会话和 response 会继续使用已固定的通道与节点。",
    body: `<div class="form-grid"><label><span>对象</span><input value="${escapeHtml(subjectLabel)}" disabled></label><label><span>目标通道</span><select id="channelOverrideTarget">${activeChannels.map((channel) => `<option value="${escapeHtml(channel.id)}">${escapeHtml(channel.name)}</option>`).join("")}</select></label><label><span>到期时间</span><input id="channelOverrideExpiry" type="datetime-local" value="${defaultExpiry}" required></label><label><span>切换原因</span><input id="channelOverrideReason" type="text" maxlength="500" placeholder="例如：direct channel canary" required></label></div>`,
    onSave: async (dialog) => {
      const channelId = dialog.querySelector<HTMLSelectElement>("#channelOverrideTarget")?.value || "";
      const expiry = dialog.querySelector<HTMLInputElement>("#channelOverrideExpiry")?.value || "";
      const reason = dialog.querySelector<HTMLInputElement>("#channelOverrideReason")?.value.trim() || "";
      await api("/api/admin/channel-overrides", {
        method: "POST",
        body: JSON.stringify({ subjectType, subjectId, channelId, expiresAt: fromDatetimeLocal(expiry), reason })
      });
      toast("临时通道切换已生效；新请求会立即使用目标通道。", "success");
      await loadAdminChannelsData();
      renderAdminChannels();
    }
  });
}

export async function revokeChannelOverride(id: string): Promise<void> {
  try {
    await api(`/api/admin/channel-overrides/${encodeURIComponent(id)}`, { method: "DELETE", body: JSON.stringify({ reason: "manual_revoke" }) });
    toast("临时通道切换已撤销；后续新请求恢复正常优先级。", "success");
    await loadAdminChannelsData();
    renderAdminChannels();
  } catch (error) {
    toast((error as Error).message, "error");
  }
}

export async function updateAdminUserApiKeyStatus(apiKeyId: string, status: "active" | "disabled"): Promise<void> {
  try {
    const data = await api<{ apiKey: ApiKey }>(`/api/admin/api-keys/${encodeURIComponent(apiKeyId)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status })
    });
    state.adminUsers = state.adminUsers.map((user) => ({
      ...user,
      apiKeys: (user.apiKeys || []).map((key) => key.id === apiKeyId ? { ...key, ...data.apiKey } : key)
    }));
    toast(status === "disabled" ? "API Key 已停用。" : "API Key 已启用。", "success");
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
    await loadAdminUsersData();
    renderAdminUsers();
  }
}

export function renderAdminDocs(): void {
  const container = document.querySelector<HTMLElement>("#adminDocsContent");
  if (!container) return;
  if (state.currentView !== "docs") return;
  container.innerHTML = renderAdminGuide();
}

/** 系统概览:今日请求 / 今日 Token / 累计 Token / 活跃用户 / 平均延迟 */
export function renderTeamOverview(): void {
  if (!els.teamOverview) return;
  const members = state.members || [];

  let totalRequests = 0;
  let totalInputTokens = 0;
  let totalOutputTokens = 0;
  let totalErrors = 0;
  let activeMembers = 0;
  let cumulativeInputTokens = 0;
  let cumulativeOutputTokens = 0;

  for (const member of members) {
    const u = member.usageToday || {};
    const reqs = Number(u.requests || 0);
    if (reqs > 0) activeMembers += 1;
    totalRequests += reqs;
    totalInputTokens += Number(u.inputTokens || 0);
    totalOutputTokens += Number(u.outputTokens || 0);
    totalErrors += Number(u.errors || 0);
    const t = member.usageTotal || ({} as UsageSummary);
    cumulativeInputTokens += Number(t.inputTokens || 0);
    cumulativeOutputTokens += Number(t.outputTokens || 0);
  }

  const totalTokens = totalInputTokens + totalOutputTokens;
  const cumulativeTokens = cumulativeInputTokens + cumulativeOutputTokens;
  const errorRate = totalRequests > 0 ? ((totalErrors / totalRequests) * 100).toFixed(1) : "0.0";
  const fmt = (n: number): string => Number(n).toLocaleString("zh-CN");
  const revenue = state.adminRechargeRevenue;

  els.teamOverview.innerHTML = `
    <div class="overview-card">
      <p class="eyebrow">今日请求</p>
      <p class="overview-value">${fmt(totalRequests)}</p>
      <button type="button" class="overview-error-link" data-action="open-today-error-requests" aria-label="查看今日 ${fmt(totalErrors)} 条错误请求">${fmt(totalErrors)} 错误 · ${errorRate}%</button>
    </div>
    <div class="overview-card">
      <p class="eyebrow">今日 Token</p>
      <p class="overview-value">${fmt(totalTokens)}</p>
      <p class="muted">输入 ${fmt(totalInputTokens)} · 输出 ${fmt(totalOutputTokens)}</p>
    </div>
    <div class="overview-card">
      <p class="eyebrow">累计 Token</p>
      <p class="overview-value">${fmt(cumulativeTokens)}</p>
      <p class="muted">输入 ${fmt(cumulativeInputTokens)} · 输出 ${fmt(cumulativeOutputTokens)}</p>
    </div>
    <div class="overview-card">
      <p class="eyebrow">活跃用户</p>
      <p class="overview-value">${fmt(activeMembers)} / ${fmt(members.length)}</p>
      <p class="muted">今日有请求的用户</p>
    </div>
    <div class="overview-card">
      <p class="eyebrow">平均延迟</p>
      <p class="overview-value">${fmtAvgLatency()}</p>
      <p class="muted">最近 10 条请求</p>
    </div>
    <div class="overview-card revenue-overview-card">
      <p class="eyebrow">收入（充值）</p>
      <dl class="revenue-period-grid">
        <div><dt>当日</dt><dd>${revenue ? formatMoney(revenue.today, revenue.currency) : "—"}</dd></div>
        <div><dt>当周</dt><dd>${revenue ? formatMoney(revenue.week, revenue.currency) : "—"}</dd></div>
        <div><dt>当月</dt><dd>${revenue ? formatMoney(revenue.month, revenue.currency) : "—"}</dd></div>
        <div><dt>累计</dt><dd>${revenue ? formatMoney(revenue.total, revenue.currency) : "—"}</dd></div>
      </dl>
    </div>
  `;
}

function fmtAvgLatency(): string {
  const logs = (state.requestLogs || []).slice(0, 10);
  if (!logs.length) return "—";
  const sum = logs.reduce((acc, l) => acc + Number(l.latencyMs || 0), 0);
  return `${Math.round(sum / logs.length)} ms`;
}

// ===== 用户列表 =====

export function renderMembers(): void {
  if (!els.memberList) return;
  if (!state.members.length) {
    els.memberList.innerHTML = `<p class="muted">还没有用户。</p>`;
    return;
  }

  const pageInfo = paginate("members", state.members);

  els.memberList.innerHTML = `
    <div class="table-wrap">
      <table class="data-table member-table">
        <thead>
          <tr>
            <th>用户</th>
            <th>状态</th>
            <th>计划</th>
            <th>试用截止</th>
            <th>Req/min</th>
            <th>今日用量</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>${pageInfo.items.map((member) => memberRows(member)).join("")}</tbody>
      </table>
    </div>
    ${renderPagination("members", pageInfo)}
  `;
}

function memberRows(member: Member): string {
  const keys = member.apiKeys || [];
  const usage = member.usageToday || {};
  const used = Number(usage.totalTokens || 0);
  const isExpanded = state.expandedMemberIds.has(member.id);

  return `
    <tr class="member-card ${isExpanded ? "expanded" : ""}" data-member-id="${escapeHtml(member.id)}">
      <td>
        <div class="member-identity">
          <button class="expand-button" data-action="toggle-member" data-id="${escapeHtml(member.id)}" aria-label="${isExpanded ? "收起用户 Key" : "展开用户 Key"}">${isExpanded ? "⌄" : "›"}</button>
          <div>
            <strong>${escapeHtml(member.name)}</strong>
            <div class="muted">${escapeHtml(member.id)}</div>
            <div class="muted">${keys.length} 个 API Key</div>
          </div>
        </div>
      </td>
      <td>${pill(member.status)}</td>
      <td>
        <select data-member-field="plan">
          <option value="trial" ${member.plan === "trial" ? "selected" : ""}>trial</option>
          <option value="paid" ${member.plan === "paid" ? "selected" : ""}>paid</option>
          <option value="internal" ${member.plan === "internal" ? "selected" : ""}>internal</option>
        </select>
      </td>
      <td><input data-member-field="trialEndsAt" type="datetime-local" value="${escapeHtml(toDatetimeLocal(member.trialEndsAt))}"></td>
      <td><input data-member-field="rateLimitPerMinute" type="number" min="0" placeholder="unlimited" value="${escapeHtml(member.rateLimitPerMinute ?? "")}"></td>
      <td>
        <div class="muted">${fmtNum(used)} tokens · ${Number(usage.requests || 0)} req · ${Number(usage.errors || 0)} err</div>
      </td>
      <td>
        <div class="table-actions">
          <button class="secondary-button" data-action="save-member" data-id="${escapeHtml(member.id)}">保存</button>
          ${
            member.status === "active"
              ? `<button class="ghost-button" data-action="disable-member" data-id="${escapeHtml(member.id)}">停用</button>`
              : `<button class="ghost-button" data-action="enable-member" data-id="${escapeHtml(member.id)}" ${member.status === "deleted" ? "disabled" : ""}>启用</button>`
          }
          <button class="ghost-button" data-action="delete-member" data-id="${escapeHtml(member.id)}" ${member.status === "deleted" ? "disabled" : ""}>删除</button>
        </div>
      </td>
    </tr>
    ${
      isExpanded
        ? `<tr class="sub-row"><td colspan="8">${renderKeyTable(keys)}</td></tr>`
        : ""
    }
  `;
}

export function renderKeyTable(keys: ApiKey[]): string {
  if (!keys.length) return `<span class="muted">还没有 API Key。</span>`;

  return `
    <table class="sub-table">
      <thead>
        <tr>
          <th>Key</th>
          <th>名称</th>
          <th>状态</th>
          <th>最近使用</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        ${keys
          .map(
            (apiKey) => {
              const plainKey = state.plainApiKeys[apiKey.id] || apiKey.plainKey || "";
              return `
              <tr>
                <td>
                  <div class="key-display">
                    <code class="key-value">${escapeHtml(plainKey || `${apiKey.keyPrefix}...`)}</code>
                    ${!plainKey ? `
                      <button class="ghost-button key-show-btn" data-action="admin-fetch-key" data-id="${escapeHtml(apiKey.id)}">获取完整 Key</button>
                    ` : `
                      <button class="ghost-button key-show-btn" data-action="admin-toggle-show-key" data-id="${escapeHtml(apiKey.id)}">隐藏 Key</button>
                    `}
                  </div>
                  <div class="muted">${escapeHtml(apiKey.id)}</div>
                </td>
                <td>${escapeHtml(apiKey.name)}</td>
                <td>${pill(apiKey.status)}</td>
                <td class="table-muted">${escapeHtml(formatTime(apiKey.lastUsedAt))}</td>
                <td>
                  <div class="table-actions">
                    ${
                      apiKey.status === "active"
                        ? `<button class="ghost-button" data-action="disable-key" data-id="${escapeHtml(apiKey.id)}">停用</button>`
                        : `<button class="ghost-button" data-action="enable-key" data-id="${escapeHtml(apiKey.id)}" ${apiKey.status === "deleted" || apiKey.status === "revoked" ? "disabled" : ""}>启用</button>`
                    }
                    <button class="secondary-button" data-action="copy-key" data-id="${escapeHtml(apiKey.id)}">复制</button>
                    <button class="ghost-button" data-action="revoke-key" data-id="${escapeHtml(apiKey.id)}" ${apiKey.status === "active" || apiKey.status === "disabled" ? "" : "disabled"}>吊销</button>
                    <button class="ghost-button" data-action="delete-key" data-id="${escapeHtml(apiKey.id)}" ${apiKey.status === "deleted" ? "disabled" : ""}>删除</button>
                  </div>
                </td>
              </tr>
            `;
            }
          )
          .join("")}
      </tbody>
    </table>
  `;
}

// ===== 请求日志 =====

export function renderRequestLogs(): void {
  if (!els.requestLogList) return;
  if (state.requestLogsLoading) {
    els.requestLogList.innerHTML = `<p class="muted">正在加载最近调用日志...</p>`;
    return;
  }
  if (!state.requestLogsLoaded) {
    els.requestLogList.innerHTML = `
      <div class="empty-state">
        <p class="muted">请求日志默认不在首屏加载，避免拖慢管理后台。点击刷新后再获取最近 80 条。</p>
        <div class="table-actions">
          <button class="secondary-button" data-action="refresh-admin">加载日志</button>
        </div>
      </div>
    `;
    return;
  }
  if (!state.requestLogs.length) {
    els.requestLogList.innerHTML = `<p class="muted">最近 80 条内还没有调用日志。</p>`;
    return;
  }

  const pageInfo = paginate("requestLogs", state.requestLogs);
  const requestLogRows = pageInfo.items
    .map((log) => requestLogRow(log))
    .join("");

  els.requestLogList.innerHTML = `
    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>时间</th>
            <th>用户 / Key</th>
            <th>状态</th>
            <th>会话</th>
            <th>耗时</th>
            <th>Tokens</th>
            <th>错误</th>
          </tr>
        </thead>
        <tbody>${requestLogRows}</tbody>
      </table>
    </div>
    ${renderPagination("requestLogs", pageInfo)}
  `;
}

export function renderTodayErrorRequests(): void {
  const panel = els.todayErrorRequests;
  if (!panel) return;
  panel.hidden = !state.todayErrorRequestsOpen;
  if (!state.todayErrorRequestsOpen) return;

  const pagination = state.todayErrorRequestsPagination;
  const start = pagination.total ? (pagination.page - 1) * pagination.pageSize + 1 : 0;
  const end = Math.min(pagination.page * pagination.pageSize, pagination.total);
  const content = state.todayErrorRequestsLoading
    ? `<p class="muted">正在加载今日错误请求...</p>`
    : !state.todayErrorRequests.length
      ? `<p class="muted">今日没有错误请求。</p>`
      : `
        <div class="table-wrap">
          <table class="data-table error-request-table">
            <thead><tr><th>时间</th><th>用户 / Key</th><th>请求</th><th>模型</th><th>错误代码</th><th>错误原因</th></tr></thead>
            <tbody>${state.todayErrorRequests.map((log) => errorRequestRow(log)).join("")}</tbody>
          </table>
        </div>
        <footer class="pagination">
          <div class="pagination-summary">共 ${pagination.total} 条，当前 ${start}-${end}；每页最多 20 条</div>
          <div class="pagination-controls">
            <button class="pagination-button" data-action="change-today-error-page" data-page="${pagination.page - 1}" ${pagination.page <= 1 ? "disabled" : ""}>上一页</button>
            <span class="pagination-page-status">第 ${pagination.page} / ${pagination.totalPages} 页</span>
            <button class="pagination-button" data-action="change-today-error-page" data-page="${pagination.page + 1}" ${pagination.page >= pagination.totalPages ? "disabled" : ""}>下一页</button>
          </div>
        </footer>`;

  panel.innerHTML = `
    <div class="panel-heading">
      <div><p class="eyebrow">Errors</p><h2>今日错误请求</h2><p class="muted">与“今日请求”的错误统计采用同一上海自然日与请求日志口径。</p></div>
      <button class="ghost-button" data-action="close-today-error-requests">收起</button>
    </div>
    ${content}
  `;
}

function errorRequestRow(log: RequestLog): string {
  const member = state.members.find((item) => item.id === log.memberId);
  const memberLabel = log.memberName || member?.name || log.memberEmail || log.memberId || "unknown";
  return `
    <tr>
      <td class="table-muted">${escapeHtml(formatTime(log.createdAt))}</td>
      <td><div>${escapeHtml(memberLabel)}</div><div class="table-muted">${escapeHtml(log.apiKeyName || "-")}</div></td>
      <td><code title="${escapeHtml(log.requestId || log.id)}">${escapeHtml(shortId(log.requestId || log.id))}</code></td>
      <td>${escapeHtml(log.model || "-")}</td>
      <td><code>${escapeHtml(log.errorCode || "-")}</code></td>
      <td class="error-request-message">${escapeHtml(log.errorMessage || "-")}</td>
    </tr>
  `;
}

function requestLogRow(log: RequestLog): string {
  const member = state.members.find((item) => item.id === log.memberId);
  const memberLabel = log.memberName || member?.name || log.memberEmail || log.memberId || "unknown";
  const keyLabel = log.apiKeyName || "-";
  const channelLabel = log.channelName || log.channelId || "未归因";

  return `
    <tr>
      <td class="table-muted">${escapeHtml(formatTime(log.createdAt))}</td>
      <td>
        <div>${escapeHtml(memberLabel)}</div>
        <div class="table-muted">${escapeHtml(keyLabel)}</div>
        <div class="table-muted">通道：${escapeHtml(channelLabel)}</div>
      </td>
      <td>${pill(log.status)}</td>
      <td class="table-muted">${escapeHtml(shortId(log.conversationId || "-"))}</td>
      <td>${Number(log.latencyMs || 0)}ms</td>
      <td>${Number(log.totalTokens || 0)} <span class="muted">(${Number(log.inputTokens || 0)} / ${Number(log.outputTokens || 0)})</span></td>
      <td class="table-muted">${escapeHtml(log.errorMessage || "-")}</td>
    </tr>
  `;
}

// ===== 分页 =====

interface PageInfo<T> {
  items: T[];
  page: number;
  pageSize: number;
  startIndex: number;
  totalItems: number;
  totalPages: number;
}

function paginate<T>(listKey: "members" | "requestLogs" | "users", items: T[]): PageInfo<T> {
  const pagination: PaginationState = state.pagination[listKey];
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pagination.pageSize));
  pagination.page = Math.min(Math.max(1, pagination.page), totalPages);

  const startIndex = (pagination.page - 1) * pagination.pageSize;
  return {
    items: items.slice(startIndex, startIndex + pagination.pageSize),
    page: pagination.page,
    pageSize: pagination.pageSize,
    startIndex,
    totalItems,
    totalPages
  };
}

function renderPagination(listKey: string, pageInfo: PageInfo<unknown>): string {
  const pageNumbers = paginationWindow(pageInfo.page, pageInfo.totalPages);
  const start = pageInfo.totalItems ? pageInfo.startIndex + 1 : 0;
  const end = Math.min(pageInfo.startIndex + pageInfo.pageSize, pageInfo.totalItems);

  return `
    <footer class="pagination" data-list="${escapeHtml(listKey)}">
      <div class="pagination-summary">
        共 ${pageInfo.totalItems} 条，当前 ${start}-${end}
      </div>
      <div class="pagination-controls">
        <label class="pagination-size">
          <span>每页</span>
          <select data-pagination-size="${escapeHtml(listKey)}">
            ${[5, 10, 20, 50]
              .map(
                (size) =>
                  `<option value="${size}" ${size === pageInfo.pageSize ? "selected" : ""}>${size}</option>`
              )
              .join("")}
          </select>
        </label>
        <button class="pagination-button" data-action="page-change" data-list="${escapeHtml(listKey)}" data-page="${pageInfo.page - 1}" ${pageInfo.page <= 1 ? "disabled" : ""}>上一页</button>
        ${pageNumbers
          .map((page) =>
            page === "gap"
              ? `<span class="pagination-gap">...</span>`
              : `<button class="pagination-button ${page === pageInfo.page ? "active" : ""}" data-action="page-change" data-list="${escapeHtml(listKey)}" data-page="${page}">${page}</button>`
          )
          .join("")}
        <button class="pagination-button" data-action="page-change" data-list="${escapeHtml(listKey)}" data-page="${pageInfo.page + 1}" ${pageInfo.page >= pageInfo.totalPages ? "disabled" : ""}>下一页</button>
      </div>
    </footer>
  `;
}

function paginationWindow(current: number, total: number): (number | "gap")[] {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);

  const pages: (number | "gap")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push("gap");
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < total - 1) pages.push("gap");
  pages.push(total);
  return pages;
}

export function changePage(listKey: "members" | "requestLogs" | "users", page: string | number): void {
  if (!state.pagination[listKey]) return;
  state.pagination[listKey].page = Number(page) || 1;
  if (listKey === "users" && state.currentView === "users") {
    window.history.replaceState({}, "", adminListUrl("users"));
  }
  renderAdminPage();
}

export function changePageSize(listKey: "members" | "requestLogs" | "users", pageSize: string | number): void {
  if (!state.pagination[listKey]) return;
  state.pagination[listKey].pageSize = Number(pageSize) || 10;
  state.pagination[listKey].page = 1;
  if (listKey === "users" && state.currentView === "users") {
    window.history.replaceState({}, "", adminListUrl("users"));
  }
  renderAdminPage();
}

/** 用户管理搜索:更新关键词并回到第一页 */
export function setAdminUsersSearch(keyword: string): void {
  state.adminUsersSearch = keyword;
  state.pagination.users.page = 1;
  if (state.currentView === "users") {
    window.history.replaceState({}, "", adminListUrl("users"));
  }
  renderAdminPage();
}

// ===== Admin 用户操作 =====

export async function createAdminUser(): Promise<void> {
  const nameInput = document.querySelector<HTMLInputElement>("#new-user-name");
  const emailInput = document.querySelector<HTMLInputElement>("#new-user-email");
  const passwordInput = document.querySelector<HTMLInputElement>("#new-user-password");
  const tokensInput = document.querySelector<HTMLInputElement>("#new-user-tokens");
  const name = nameInput?.value.trim() || "";
  const email = emailInput?.value.trim() || "";
  const password = passwordInput?.value || "";
  const initialTokens = tokensInput?.value ? Number(tokensInput.value) : 0;

  if (!name || !email || !password) {
    toast("请填写用户名、邮箱和初始密码。", "error");
    return;
  }
  if (password.length < 8) {
    toast("初始密码至少 8 位。", "error");
    return;
  }
  if (!Number.isSafeInteger(initialTokens) || initialTokens < 0) {
    toast("初始充值点数必须是非负整数。", "error");
    return;
  }

  setBusy(true);
  try {
    const data = await api<{ user: User }>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify({ name, email, password, initialTokens })
    });
    state.adminUsers = [...state.adminUsers, data.user];
    if (nameInput) nameInput.value = "";
    if (emailInput) emailInput.value = "";
    if (passwordInput) passwordInput.value = "";
    if (tokensInput) tokensInput.value = "";
    toast("用户已新增。", "success");
    closeModal();
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export function openCreateUserModal(): void {
  els.createUserForm?.reset();
  openModal("createUser");
}

export async function resetAdminUserPassword(userId: string, userName: string): Promise<void> {
  const password = prompt(`请输入 "${userName}" 的新密码(至少 8 位):`) || "";
  if (!password) return;
  if (password.length < 8) {
    toast("新密码至少 8 位。", "error");
    return;
  }

  setBusy(true);
  try {
    await api(`/api/admin/users/${encodeURIComponent(userId)}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password })
    });
    toast(`"${userName}" 的密码已重置。`, "success");
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export function openRechargeUserModal(userId: string, userName: string): void {
  els.rechargeUserForm?.reset();
  const idInput = document.querySelector<HTMLInputElement>("#recharge-user-id");
  const nameEl = document.querySelector<HTMLElement>("#recharge-user-name");
  const planInput = document.querySelector<HTMLSelectElement>("#recharge-user-plan");
  const cashInput = document.querySelector<HTMLInputElement>("#recharge-user-cash");
  if (idInput) idInput.value = userId;
  if (nameEl) nameEl.textContent = userName;
  const plans = activePaidRechargePlans();
  if (planInput) {
    planInput.innerHTML = plans.length
      ? plans.map((plan) => `
        <option value="${escapeHtml(plan.id)}" ${plan.isFeatured ? "selected" : ""}>
          ${escapeHtml(plan.name)} · 实付 ${formatMoney(plan.price, plan.currency)} / 票面 ${formatMoney(plan.faceAmount ?? plan.price, plan.currency)} = ${fmtNum(plan.tokenAmount)} 点
        </option>
      `).join("")
      : `<option value="">暂无可用充值方案</option>`;
    if (plans.length && !plans.some((plan) => plan.isFeatured)) {
      planInput.value = plans[0].id;
    }
    planInput.onchange = updateRechargePointsPreview;
  }
  if (cashInput) {
    cashInput.oninput = updateRechargePointsPreview;
  }
  updateRechargePointsPreview();
  openModal("rechargeUser");
}

export async function rechargeAdminUser(): Promise<void> {
  const idInput = document.querySelector<HTMLInputElement>("#recharge-user-id");
  const planInput = document.querySelector<HTMLSelectElement>("#recharge-user-plan");
  const cashInput = document.querySelector<HTMLInputElement>("#recharge-user-cash");
  const noteInput = document.querySelector<HTMLInputElement>("#recharge-user-note");
  const nameEl = document.querySelector<HTMLElement>("#recharge-user-name");
  const userId = idInput?.value || "";
  const userName = nameEl?.textContent?.trim() || userId;
  const cashAmount = Number(cashInput?.value || 0);
  if (!Number.isFinite(cashAmount) || cashAmount <= 0) {
    toast("充值金额必须大于 0。", "error");
    return;
  }
  if (!planInput?.value) {
    toast("请选择有效的充值方案。", "error");
    return;
  }
  const note = noteInput?.value.trim() || "";

  setBusy(true);
  try {
    const data = await api<{
      wallet: NonNullable<User["wallet"]>;
      transactions: WalletTransaction[];
      topup: { points: number; cashAmount: number; currency: string };
    }>(`/api/admin/users/${encodeURIComponent(userId)}/recharge`, {
      method: "POST",
      body: JSON.stringify({ cashAmount, currency: "CNY", planId: planInput.value, note })
    });
    state.adminUsers = state.adminUsers.map((user) =>
      user.id === userId
        ? { ...user, wallet: data.wallet }
        : user
    );
    toast(`已为 "${userName}" 充值 ${formatMoney(data.topup.cashAmount, data.topup.currency)} / ${fmtNum(data.topup.points)} 点。`, "success");
    closeModal();
    renderAdminUsers();
    void loadAdminWalletTransactions()
      .then(() => renderAdminWallets())
      .catch((error) => {
        console.error("refresh admin wallet transactions failed:", error);
      });
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

// ===== 用户操作 =====

export function openCreateApiKeyModal(memberId: string): void {
  state.pendingApiKeyMemberId = memberId;
  state.pendingApiKeyIsUser = false;
  if (els.apiKeyName) els.apiKeyName.value = "";
  openModal("apiKey");
}

export async function createApiKey(event: Event): Promise<void> {
  event.preventDefault();
  const name = els.apiKeyName?.value.trim() ?? "";

  if (!name) {
    toast("请输入 Key 名称。");
    return;
  }

  // 用户自助创建 Key 走独立分支
  if (state.pendingApiKeyIsUser) {
    await handleCreateUserKey();
    return;
  }

  const memberId = state.pendingApiKeyMemberId;
  if (!memberId) {
    toast("请先选择用户。");
    return;
  }

  setBusy(true);
  try {
    const data = await api<{ apiKey: ApiKey; plainKey: string }>(`/admin/members/${encodeURIComponent(memberId)}/api-keys`, {
      method: "POST",
      body: JSON.stringify({ name })
    });
    state.plainApiKeys[data.apiKey.id] = data.plainKey;
    saveStoredPlainApiKeys(state.plainApiKeys);
    if (els.plainKey) els.plainKey.textContent = data.plainKey;
    state.expandedMemberIds.add(memberId);
    state.pendingApiKeyMemberId = "";
    if (els.apiKeyName) els.apiKeyName.value = "";
    openModal("key");
    toast("API Key 已生成，只显示这一次。");
    await loadAll();
  } catch (error) {
    toast((error as Error).message);
  } finally {
    setBusy(false);
  }
}

export async function saveMember(memberId: string): Promise<void> {
  const card = document.querySelector(`.member-card[data-member-id="${CSS.escape(memberId)}"]`);
  if (!card) return;

  const getField = (field: string): string | undefined =>
    card.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-member-field="${field}"]`)?.value;
  const rateLimitPerMinute = getField("rateLimitPerMinute");

  setBusy(true);
  try {
    await api(`/admin/members/${encodeURIComponent(memberId)}`, {
      method: "PATCH",
      body: JSON.stringify({
        plan: getField("plan"),
        trialEndsAt: fromDatetimeLocal(getField("trialEndsAt") ?? ""),
        rateLimitPerMinute: rateLimitPerMinute ? Number(rateLimitPerMinute) : null
      })
    });
    toast("用户策略已保存。");
    await loadAll();
  } catch (error) {
    toast((error as Error).message);
  } finally {
    setBusy(false);
  }
}

export async function memberAction(memberId: string, action: string): Promise<void> {
  setBusy(true);
  try {
    await api(`/admin/members/${encodeURIComponent(memberId)}/${action}`, {
      method: "POST"
    });
    toast("用户状态已更新。");
    await loadAll();
  } catch (error) {
    toast((error as Error).message);
  } finally {
    setBusy(false);
  }
}

export async function revokeApiKey(apiKeyId: string): Promise<void> {
  await keyAction(apiKeyId, "revoke", "API Key 已吊销。");
}

export async function keyAction(apiKeyId: string, action: string, message = "API Key 状态已更新。"): Promise<void> {
  setBusy(true);
  try {
    await api(`/admin/api-keys/${encodeURIComponent(apiKeyId)}/${action}`, {
      method: "POST"
    });
    toast(message);
    await loadAll();
  } catch (error) {
    toast((error as Error).message);
  } finally {
    setBusy(false);
  }
}

function findApiKey(apiKeyId: string): ApiKey | undefined {
  for (const member of state.members) {
    const apiKey = (member.apiKeys || []).find((item) => item.id === apiKeyId);
    if (apiKey) return apiKey;
  }
  return undefined;
}

export function toggleMember(memberId: string): void {
  if (state.expandedMemberIds.has(memberId)) {
    state.expandedMemberIds.delete(memberId);
  } else {
    state.expandedMemberIds.add(memberId);
  }
  renderMembers();
}

// ===== Admin 扩展视图 =====

export function renderAdminUsers(): void {
  closeAdminUserActionMenu();
  if (state.currentView === "userDetail") {
    renderAdminUserDetail();
    return;
  }
  if (!els.adminUsersContent) return;
  if (state.currentView !== "users") return;
  const allUsers = state.adminUsers || [];
  const capturePolicy = state.adminInteractionCapturePolicy;

  // 按用户名 / 邮箱过滤(大小写不敏感、子串匹配)
  const keyword = state.adminUsersSearch.trim().toLowerCase();
  const filtered = keyword
    ? allUsers.filter((u) => {
        const name = (u.name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        return name.includes(keyword) || email.includes(keyword);
      })
    : allUsers;

  const pageInfo = paginate("users", filtered);

  els.adminUsersContent.innerHTML = `
    <section class="panel panel-fill list-panel">
      <div class="panel-heading">
        <div><p class="eyebrow">Users</p><h2>用户管理 (${allUsers.length})</h2></div>
        <div class="panel-actions">
          <button class="secondary-button" data-action="refresh-admin">刷新</button>
          <button class="primary-button" data-action="open-create-user-modal">新增用户</button>
        </div>
      </div>
      <div class="capture-policy-banner ${capturePolicy?.enabled ? "is-enabled" : "is-disabled"}">
        <div>
          <span class="capture-policy-status">${capturePolicy?.enabled ? "采集总开关已开启" : "采集总开关已关闭"}</span>
          <small>${capturePolicy?.enabled
            ? "仅采集已获用户允许，且未被 API Key 单独关闭的交互。"
            : "所有用户和 API Key 都不会写入交互采集目录。"}</small>
          ${capturePolicy && !capturePolicy.runtimeEnabled
            ? `<small class="capture-policy-warning">当前 admin-api 的运维开关未启用，即使打开这里也不会写入。</small>`
            : ""}
        </div>
        <button
          class="${capturePolicy?.enabled ? "secondary-button" : "primary-button"} compact-button"
          data-action="set-global-interaction-capture"
          data-enabled="${capturePolicy?.enabled ? "false" : "true"}"
        >${capturePolicy?.enabled ? "关闭全部采集" : "开启采集总开关"}</button>
      </div>
      <div class="users-toolbar">
        <input
          id="adminUsersSearch"
          type="search"
          class="users-search-input"
          placeholder="按用户名或邮箱搜索"
          value="${escapeHtml(state.adminUsersSearch)}"
        >
        <span class="muted users-toolbar-summary">
          ${keyword ? `匹配 ${pageInfo.totalItems} / ${allUsers.length} 人` : `共 ${allUsers.length} 人`}
        </span>
      </div>
      ${pageInfo.items.length
        ? `<div class="admin-user-table-wrap">
            <table class="admin-user-table">
              <thead>
                <tr>
                  <th>用户</th>
                  <th>状态</th>
                  <th>钱包余额</th>
                  <th>今日 Token</th>
                  <th>本月 Token</th>
                  <th>Key</th>
                  <th>用户通道</th>
                  <th>最近活动</th>
                  <th><span class="sr-only">操作</span></th>
                </tr>
              </thead>
              <tbody>${pageInfo.items.map((u) => renderAdminUserTableRows(u)).join("")}</tbody>
            </table>
          </div>`
        : `<p class="muted pad">没有匹配的用户。</p>`}
      ${renderPagination("users", pageInfo)}
    </section>
  `;
}

function channelDisplayName(channelId: string | null | undefined, fallback: string): string {
  if (!channelId) return fallback;
  return state.adminChannels.find((channel) => channel.id === channelId)?.name || channelId;
}

function userLastActivity(user: User, keys: ApiKey[] = user.apiKeys || []): string | null {
  if (user.lastActivityAt) return user.lastActivityAt;
  const timestamps = keys
    .map((key) => key.lastUsedAt ? Date.parse(key.lastUsedAt) : Number.NaN)
    .filter((value) => Number.isFinite(value));
  if (!timestamps.length) return null;
  return new Date(Math.max(...timestamps)).toISOString();
}

function limitUsed(summary: User["usageLimit"] | ApiKey["usageLimit"], period: "day" | "month"): number {
  if (!summary) return 0;
  return Number(summary[period].settledCredits || 0) + Number(summary[period].reservedCredits || 0);
}

function limitPercent(summary: User["usageLimit"] | ApiKey["usageLimit"], period: "day" | "month"): number | null {
  if (!summary) return null;
  const limit = period === "day" ? summary.limits.dailyCreditLimit : summary.limits.monthlyCreditLimit;
  if (limit === null || limit <= 0) return null;
  return Math.max(0, Math.round(limitUsed(summary, period) / limit * 100));
}

function limitTone(percent: number | null): string {
  if (percent === null) return "";
  if (percent >= 90) return "is-danger";
  if (percent >= 70) return "is-warning";
  return "is-healthy";
}

function renderLimitPanel(summary: User["usageLimit"] | ApiKey["usageLimit"], period: "day" | "month", label: string): string {
  if (!summary) return `<article class="drawer-stat"><span>${label}</span><strong>数据不可用</strong></article>`;
  const limit = period === "day" ? summary.limits.dailyCreditLimit : summary.limits.monthlyCreditLimit;
  const used = limitUsed(summary, period);
  const percent = limitPercent(summary, period);
  return `
    <article class="drawer-stat limit-stat ${limitTone(percent)}">
      <span>${label}</span>
      <strong>${formatWalletCredits(used)} / ${limit === null ? "不限" : formatWalletCredits(limit)}</strong>
      ${percent === null
        ? `<small>未设置额度上限</small>`
        : `<span class="compact-limit-track"><i style="width:${Math.min(100, percent)}%"></i></span><small>已使用 ${percent}%（含预占）</small>`}
    </article>
  `;
}

function limitConfigValue(value: number | null | undefined, unit: "credits" | "requests"): string {
  if (value === null || value === undefined) return "不限制";
  return unit === "credits" ? `${formatWalletCredits(value)} credits` : `${fmtNum(value)} 个请求`;
}

function strictestLimitConfig(...configs: Array<UsageLimitConfig | undefined>): UsageLimitConfig {
  const strictest = (field: keyof Pick<UsageLimitConfig, "dailyCreditLimit" | "monthlyCreditLimit" | "maxConcurrentRequests">) => {
    const values = configs.map((config) => config?.[field]).filter((value): value is number => value !== null && value !== undefined);
    return values.length ? Math.min(...values) : null;
  };
  return {
    dailyCreditLimit: strictest("dailyCreditLimit"),
    monthlyCreditLimit: strictest("monthlyCreditLimit"),
    maxConcurrentRequests: strictest("maxConcurrentRequests")
  };
}

function renderKeyLimitLayers(summary: ApiKey["usageLimit"], accountSummary: User["usageLimit"]): string {
  if (!summary) return `<p class="muted">限制数据不可用。</p>`;
  const admin = summary.adminLimits;
  const user = summary.userLimits || summary.limits;
  const account = accountSummary?.limits;
  const effective = strictestLimitConfig(account, summary.effectiveLimits || summary.limits);
  const rows = [
    ["单日费用", "dailyCreditLimit", "credits"],
    ["单月费用", "monthlyCreditLimit", "credits"],
    ["最大并发", "maxConcurrentRequests", "requests"]
  ] as const;
  return `
    <div class="limit-layer-table" role="table" aria-label="Key 限制分层">
      <div class="limit-layer-row limit-layer-head" role="row">
        <span role="columnheader">限制项</span>
        <span role="columnheader">账户总限制</span>
        <span role="columnheader">管理员强制上限</span>
        <span role="columnheader">用户自设上限</span>
        <span role="columnheader">最终生效</span>
      </div>
      ${rows.map(([label, field, unit]) => `
        <div class="limit-layer-row" role="row">
          <strong role="cell">${label}</strong>
          <span role="cell">${limitConfigValue(account?.[field], unit)}</span>
          <span role="cell">${limitConfigValue(admin?.[field], unit)}</span>
          <span role="cell">${limitConfigValue(user?.[field], unit)}</span>
          <span class="limit-effective-value" role="cell">${limitConfigValue(effective?.[field], unit)}</span>
        </div>
      `).join("")}
    </div>
  `;
}

function keyPolicyPresentation(key: ApiKey, user: User): {
  summary: string;
  compression: string;
  compressionSource: string;
  optimization: string;
  optimizationSource: string;
} {
  const keyCompression = parsePolicy(key.compressionPolicy);
  const userCompression = parsePolicy(user.compressionPolicy);
  const keyOptimization = parsePolicy(key.compressionOptimizationPolicy);
  const userOptimization = parsePolicy(user.compressionOptimizationPolicy);
  const keyCompressionMode = String(keyCompression.mode || "");
  const userCompressionMode = String(userCompression.mode || "");
  const keyHasCompression = Boolean(keyCompressionMode);
  const userHasCompression = Boolean(userCompressionMode);
  const keyHasOptimization = Object.hasOwn(keyOptimization, "enabled");
  const userHasOptimization = Object.hasOwn(userOptimization, "enabled");
  const overrideCount = Number(keyHasCompression) + Number(keyHasOptimization);
  return {
    summary: overrideCount ? `Key 覆盖 ${overrideCount} 项` : (userHasCompression || userHasOptimization ? "继承用户策略" : "继承系统默认"),
    compression: compressionModeLabel(keyHasCompression ? keyCompressionMode : userCompressionMode),
    compressionSource: keyHasCompression ? "Key 覆盖" : (userHasCompression ? "继承用户" : "系统默认"),
    optimization: optimizationPolicyLabel(keyHasOptimization ? keyOptimization : userOptimization),
    optimizationSource: keyHasOptimization ? "Key 覆盖" : (userHasOptimization ? "继承用户" : "系统默认")
  };
}

function renderAdminUserTableRows(u: User): string {
  const keys = u.apiKeys || [];
  const today = u.todayUsage || { requests: 0, totalTokens: 0, errors: 0 } as UsageSummary;
  const month = u.monthUsage || { requests: 0, totalTokens: 0, errors: 0 } as UsageSummary;
  const wallet = u.wallet;
  const walletBalance = wallet ? `${formatWalletCredits(wallet.balance || 0)} credits` : "0 credits";
  const lastActivity = userLastActivity(u, keys);
  return `
    <tr class="admin-user-row">
      <td>
        <a class="user-name-button" href="${escapeHtml(adminUserDetailUrl(u.id, "usage"))}" data-action="open-admin-user-detail" data-id="${escapeHtml(u.id)}">
          <strong>${escapeHtml(u.name || "?")}</strong>
          <small>${escapeHtml(u.email || "无邮箱")}</small>
        </a>
      </td>
      <td>${pill(u.status)}<div class="role-line">${parseUserRoles(u).map((role) => escapeHtml(role)).join(" · ")}</div></td>
      <td><strong>${escapeHtml(walletBalance)}</strong></td>
      <td><strong>${fmtNum(today.totalTokens || 0)}</strong><small>${today.requests || 0} 请求 · ${today.errors || 0} 错误</small></td>
      <td><strong>${fmtNum(month.totalTokens || 0)}</strong><small>${month.requests || 0} 请求 · ${month.errors || 0} 错误</small></td>
      <td><strong>${keys.length}</strong></td>
      <td><span class="channel-name">${escapeHtml(channelDisplayName(u.defaultChannelId, "系统默认"))}</span></td>
      <td><span class="table-muted">${lastActivity ? escapeHtml(formatTime(lastActivity)) : "尚未使用"}</span></td>
      <td>
        <div class="user-row-actions">
          <a class="secondary-button compact-button" href="${escapeHtml(adminUserDetailUrl(u.id, "usage"))}" data-action="open-admin-user-detail" data-id="${escapeHtml(u.id)}">查看详情</a>
          <button class="user-row-more-trigger" data-action="toggle-admin-user-menu" data-id="${escapeHtml(u.id)}" aria-label="更多操作" aria-expanded="false">•••</button>
        </div>
      </td>
    </tr>
  `;
}

export function closeAdminUserActionMenu(): boolean {
  const menu = document.querySelector<HTMLElement>("#adminUserActionMenu");
  if (!menu) return false;
  document.querySelectorAll<HTMLElement>('[data-action="toggle-admin-user-menu"][aria-expanded="true"]')
    .forEach((trigger) => trigger.setAttribute("aria-expanded", "false"));
  menu.remove();
  return true;
}

export function toggleAdminUserActionMenu(trigger: HTMLElement, userId: string): void {
  const user = findAdminUser(userId);
  if (!user) return;
  const current = document.querySelector<HTMLElement>("#adminUserActionMenu");
  if (current?.dataset.userId === userId) {
    closeAdminUserActionMenu();
    return;
  }
  closeAdminUserActionMenu();

  const displayName = user.name || user.email || user.id;
  const menu = document.createElement("div");
  menu.id = "adminUserActionMenu";
  menu.className = "user-row-more-menu";
  menu.dataset.userId = userId;
  menu.setAttribute("role", "menu");
  menu.setAttribute("aria-label", `${displayName} 的操作`);
  menu.innerHTML = `
    <button role="menuitem" data-action="recharge-user" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(displayName)}">充值</button>
    <button role="menuitem" data-action="create-user-api-key" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(displayName)}">创建 Key</button>
    <button role="menuitem" data-action="reset-user-password" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(displayName)}">重置密码</button>
    <button role="menuitem" data-action="toggle-user-status" data-id="${escapeHtml(user.id)}" data-status="${user.status === "active" ? "disabled" : "active"}">${user.status === "active" ? "停用用户" : "启用用户"}</button>
    <button role="menuitem" class="danger-action" data-action="delete-user" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(displayName)}">删除用户</button>
  `;
  document.body.appendChild(menu);
  trigger.setAttribute("aria-expanded", "true");

  const triggerRect = trigger.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();
  const viewportPadding = 8;
  const gap = 6;
  const left = Math.min(
    Math.max(viewportPadding, triggerRect.right - menuRect.width),
    window.innerWidth - menuRect.width - viewportPadding
  );
  const fitsBelow = window.innerHeight - triggerRect.bottom >= menuRect.height + gap + viewportPadding;
  const top = fitsBelow
    ? triggerRect.bottom + gap
    : Math.max(viewportPadding, triggerRect.top - menuRect.height - gap);
  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
  menu.dataset.placement = fitsBelow ? "bottom" : "top";
}

function usageSummary(value: UsageSummary | undefined): UsageSummary {
  return value || { requests: 0, errors: 0, inputTokens: 0, outputTokens: 0, totalTokens: 0 };
}

function renderUsagePeriodCard(label: string, value: UsageSummary | undefined, hint: string): string {
  const summary = usageSummary(value);
  return `
    <article class="usage-period-card">
      <div class="usage-period-card-head"><span>${label}</span><small>${hint}</small></div>
      <strong>${fmtNum(summary.totalTokens || 0)}</strong>
      <p>Token</p>
      <dl>
        <div><dt>输入</dt><dd>${fmtNum(summary.inputTokens || 0)}</dd></div>
        <div><dt>输出</dt><dd>${fmtNum(summary.outputTokens || 0)}</dd></div>
        <div><dt>请求</dt><dd>${fmtNum(summary.requests || 0)}</dd></div>
        <div><dt>错误</dt><dd>${fmtNum(summary.errors || 0)}</dd></div>
      </dl>
    </article>
  `;
}

function renderAdminUserDetail(): void {
  if (!els.adminUserDetailContent || state.currentView !== "userDetail") return;
  const user = state.adminSelectedUserId ? findAdminUser(state.adminSelectedUserId) : null;
  if (!user) {
    els.adminUserDetailContent.innerHTML = `
      <section class="panel panel-fill admin-user-detail-empty">
        <p class="eyebrow">User detail</p>
        <h2>用户不存在或已被删除</h2>
        <p class="muted">返回用户列表重新选择。</p>
        <button class="secondary-button" data-action="return-admin-users">返回用户列表</button>
      </section>
    `;
    return;
  }
  const keys = user.apiKeys || [];
  const lastActivity = userLastActivity(user, keys);
  const tab = state.adminUserDetailTab;
  els.adminUserDetailContent.innerHTML = `
    <section class="admin-user-detail-shell">
      <header class="admin-user-detail-header">
        <div class="admin-user-detail-identity">
          <button class="detail-back-button" data-action="return-admin-users" aria-label="返回用户列表">←</button>
          <div>
            <p class="eyebrow">User detail</p>
            <div class="detail-title-line"><h2>${escapeHtml(user.name || user.email || user.id)}</h2>${pill(user.status)}</div>
            <p>${escapeHtml(user.email || "无邮箱")} · ${parseUserRoles(user).map((role) => escapeHtml(role)).join(" / ") || "普通用户"}</p>
          </div>
        </div>
        <div class="panel-actions">
          <button class="secondary-button" data-action="recharge-user" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(user.name || user.email || user.id)}">充值</button>
          <button class="primary-button" data-action="create-user-api-key" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(user.name || user.email || user.id)}">创建 Key</button>
        </div>
      </header>
      <div class="admin-user-detail-facts">
        <article><span>钱包余额</span><strong>${formatWalletCredits(user.wallet?.balance || 0)}</strong><small>credits</small></article>
        <article><span>API Keys</span><strong>${keys.length}</strong><small>${keys.filter((key) => key.status === "active").length} 个启用</small></article>
        <article class="user-channel-fact"><span>用户通道</span><select data-channel-assignment="user" data-id="${escapeHtml(user.id)}" aria-label="用户通道">${channelOptions(user.defaultChannelId, "系统默认")}</select><small>网页 Chat 与未单独设置通道的 Key</small><button class="ghost-button compact-button" data-action="open-temporary-channel-override" data-subject-type="member" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(user.name || user.email || user.id)}">临时切换</button></article>
        <article><span>最近活动</span><strong>${lastActivity ? escapeHtml(formatTime(lastActivity)) : "尚未使用"}</strong><small>按 Key 最近调用时间</small></article>
      </div>
      <nav class="admin-user-detail-tabs" aria-label="用户详情分类">
        <button class="${tab === "usage" ? "active" : ""}" data-action="set-admin-user-detail-tab" data-tab="usage">使用统计</button>
        <button class="${tab === "policy-limits" ? "active" : ""}" data-action="set-admin-user-detail-tab" data-tab="policy-limits">策略与限额</button>
        <button class="${tab === "keys" ? "active" : ""}" data-action="set-admin-user-detail-tab" data-tab="keys">API Keys <span>${keys.length}</span></button>
      </nav>
      <div class="admin-user-detail-body">
        ${tab === "usage" ? renderAdminUserUsageTab(user) : tab === "policy-limits" ? renderAdminUserPolicyLimitsTab(user) : renderAdminUserKeysTab(user)}
      </div>
    </section>
    ${renderAdminKeyDrawer()}
  `;
}

function renderAdminUserUsageTab(user: User): string {
  const period = state.adminModelUsagePeriod;
  const labels: Record<keyof ModelUsagePeriods, string> = { day: "当天", week: "本周", month: "本月", total: "历史" };
  return `
    <section class="user-detail-section">
      <div class="usage-period-grid">
        ${renderUsagePeriodCard("当日", user.todayUsage, "今天 00:00 起")}
        ${renderUsagePeriodCard("当周", user.weekUsage, "周一 00:00 起")}
        ${renderUsagePeriodCard("当月", user.monthUsage, "自然月 1 日起")}
        ${renderUsagePeriodCard("累计", user.totalUsage, "全部历史")}
      </div>
      <div class="usage-finance-panel">
        <div><p class="eyebrow">Credits</p><h3>费用与余额</h3></div>
        <dl>
          <div><dt>今日已用</dt><dd>${formatWalletCredits(limitUsed(user.usageLimit, "day"))} credits</dd></div>
          <div><dt>本月已用</dt><dd>${formatWalletCredits(limitUsed(user.usageLimit, "month"))} credits</dd></div>
          <div><dt>钱包余额</dt><dd>${formatWalletCredits(user.wallet?.balance || 0)} credits</dd></div>
        </dl>
      </div>
    </section>
    <section class="user-detail-section model-usage-admin-section">
      <div class="detail-section-heading model-usage-heading"><div><p class="eyebrow">Model usage</p><h3>模型使用比例</h3><small>仅统计成功调用；每张图分别按调用次数和 Token 总数计算占比。</small></div><div class="model-usage-periods">${(Object.keys(labels) as Array<keyof ModelUsagePeriods>).map((key) => `<button class="ghost-button compact-button ${key === period ? "active" : ""}" data-action="set-admin-model-usage-period" data-period="${key}">${labels[key]}</button>`).join("")}</div></div>
      ${renderModelUsageCharts(user.modelUsage?.[period])}
    </section>
    <section class="user-detail-section model-usage-admin-section">
      <div class="detail-section-heading"><div><p class="eyebrow">API Keys</p><h3>各 Key 的模型使用比例</h3></div></div>
      <div class="model-usage-key-list">${(user.apiKeys || []).length ? (user.apiKeys || []).map((key) => `
        <section class="model-usage-key-section"><header><div><h3>${escapeHtml(key.name)}</h3><p><code>${escapeHtml(key.keyPrefix)}</code></p></div><span>${fmtNum(key.modelUsage?.[period]?.requests || 0)} 次调用 · ${fmtNum(key.modelUsage?.[period]?.totalTokens || 0)} Token</span></header>${renderModelUsageCharts(key.modelUsage?.[period])}</section>
      `).join("") : `<p class="muted">暂无 API Key。</p>`}</div>
    </section>
  `;
}

export function setAdminModelUsagePeriod(period: string): void {
  if (!["day", "week", "month", "total"].includes(period)) return;
  state.adminModelUsagePeriod = period as keyof ModelUsagePeriods;
  renderAdminUsers();
}

function renderAdminUserPolicyLimitsTab(user: User): string {
  const compression = user.compressionToday || { requests: 0, applied: 0, errors: 0, estimatedSavedTokens: 0 };
  const risk = user.compressionRisk || { requests: 0, failed: 0 };
  const compressionPolicy = parsePolicy(user.compressionPolicy);
  const optimizationPolicy = parsePolicy(user.compressionOptimizationPolicy);
  return `
    <section class="user-detail-section">
      <div class="capture-subject-control">
        <div><p class="eyebrow">Interaction capture</p><h3>用户交互采集</h3><small>控制网页 Chat，并作为该用户 API Key 的默认值；Key 可以单独覆盖。</small></div>
        <div class="capture-subject-actions">
          <span class="policy-source-pill">${user.interactionCaptureEnabled === false ? "用户已关闭" : "用户已开启"}</span>
          <button class="secondary-button compact-button" data-action="set-user-interaction-capture" data-id="${escapeHtml(user.id)}" data-enabled="${user.interactionCaptureEnabled === false ? "true" : "false"}">${user.interactionCaptureEnabled === false ? "开启采集" : "关闭采集"}</button>
        </div>
      </div>
      ${state.adminInteractionCapturePolicy?.enabled === false ? `<p class="drawer-callout">采集总开关当前关闭；此用户设置会保留，但暂不生效。</p>` : ""}
    </section>
    <section class="user-detail-section">
      <div class="detail-section-heading"><div><p class="eyebrow">Policy</p><h3>压缩策略</h3><small>Key 未覆盖的压缩字段继续继承这里。</small></div><button class="primary-button compact-button" data-action="open-policy-settings" data-scope="user" data-id="${escapeHtml(user.id)}">编辑策略</button></div>
      <div class="effective-policy-list">
        <article><span>上下文压缩</span><strong>${escapeHtml(compressionModeLabel(String(compressionPolicy.mode || "")))}</strong><small>${Object.hasOwn(compressionPolicy, "mode") ? "用户配置" : "系统默认"}</small></article>
        <article><span>策略优化</span><strong>${escapeHtml(optimizationPolicyLabel(optimizationPolicy))}</strong><small>${Object.hasOwn(optimizationPolicy, "enabled") ? "用户配置" : "系统默认"}</small></article>
      </div>
      <div class="policy-observation-grid">
        <article><span>今日压缩</span><strong>${compression.applied || 0} / ${compression.requests || 0}</strong><small>节省 ${fmtNum(compression.estimatedSavedTokens || 0)} Token</small></article>
        <article><span>近期风险</span><strong>${risk.failed || 0} / ${risk.requests || 0}</strong><small>失败请求 / 观察请求</small></article>
      </div>
    </section>
    <section class="user-detail-section">
      <div class="detail-section-heading"><div><p class="eyebrow">Limits</p><h3>账户总限额</h3><small>所有网页 Chat 与 API Key 共同遵守；费用按 credits 控制。</small></div><button class="primary-button compact-button" data-action="edit-admin-user-usage-limits" data-id="${escapeHtml(user.id)}">编辑限额</button></div>
      <div class="detail-limit-grid">${renderLimitPanel(user.usageLimit, "day", "本日费用额度")}${renderLimitPanel(user.usageLimit, "month", "本月费用额度")}<article class="drawer-stat"><span>最大并发</span><strong>${user.usageLimit?.currentConcurrency || 0} / ${user.usageLimit?.limits.maxConcurrentRequests ?? "不限"}</strong><small>当前处理请求 / 生效上限</small></article></div>
      <p class="drawer-callout">账户限额是总闸门；单个 Key 可以设置更严格的子限额，但不能突破账户上限。</p>
      <div class="detail-security-actions"><span>账号管理</span><button class="secondary-button" data-action="reset-user-password" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(user.name || user.email || user.id)}">重置密码</button><button class="secondary-button" data-action="toggle-user-status" data-id="${escapeHtml(user.id)}" data-status="${user.status === "active" ? "disabled" : "active"}">${user.status === "active" ? "停用用户" : "启用用户"}</button><button class="ghost-button danger-button" data-action="delete-user" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(user.name || user.email || user.id)}">删除用户</button></div>
    </section>
  `;
}

function renderAdminUserKeysTab(user: User): string {
  const keys = user.apiKeys || [];
  return `
    <section class="user-detail-section user-key-section">
      <div class="detail-section-heading"><div><p class="eyebrow">API Keys</p><h3>Key 列表</h3><small>用户策略是默认层；每个 Key 可单独覆盖策略、通道和更严格的限额。</small></div><button class="primary-button compact-button" data-action="create-user-api-key" data-id="${escapeHtml(user.id)}" data-name="${escapeHtml(user.name || user.email || user.id)}">创建 Key</button></div>
      ${keys.length ? renderAdminKeyTable(keys, user) : `<div class="detail-empty-state"><strong>还没有 API Key</strong><span>创建后可单独查看用量并配置 Key 级策略与限额。</span></div>`}
    </section>
  `;
}

function renderAdminKeyTable(keys: ApiKey[], user: User): string {
  return `
    <div class="admin-key-table-wrap">
      <table class="admin-key-table">
        <thead><tr><th>Key</th><th>状态</th><th>通道</th><th>策略来源</th><th>Token 用量</th><th>Credits</th><th>请求 / 错误</th><th>最近使用</th><th></th></tr></thead>
        <tbody>${keys.map((key) => {
          const today = usageSummary(key.todayUsage);
          const week = usageSummary(key.weekUsage);
          const month = usageSummary(key.monthUsage);
          const total = usageSummary(key.totalUsage);
          const policy = keyPolicyPresentation(key, user);
          return `
            <tr>
              <td><strong>${escapeHtml(key.name)}</strong><small><code>${escapeHtml(key.keyPrefix)}</code></small></td>
              <td>${pill(key.status)}</td>
              <td><strong>${escapeHtml(channelDisplayName(key.channelId || user.defaultChannelId, "系统默认"))}</strong><small>${key.channelId ? "Key 覆盖" : (user.defaultChannelId ? "继承用户通道" : "继承系统默认")}</small></td>
              <td><span class="policy-source-pill">${escapeHtml(policy.summary)}</span></td>
              <td class="key-token-usage-cell">
                <div class="key-token-periods">
                  <span><small>日</small><strong>${fmtNum(today.totalTokens || 0)}</strong></span>
                  <span><small>周</small><strong>${fmtNum(week.totalTokens || 0)}</strong></span>
                  <span><small>月</small><strong>${fmtNum(month.totalTokens || 0)}</strong></span>
                  <span><small>累计</small><strong>${fmtNum(total.totalTokens || 0)}</strong></span>
                </div>
              </td>
              <td><strong>${formatWalletCredits(limitUsed(key.usageLimit, "day"))}</strong><small>今日 · 本月 ${formatWalletCredits(limitUsed(key.usageLimit, "month"))}</small></td>
              <td><strong>${fmtNum(total.requests || 0)} / ${fmtNum(total.errors || 0)}</strong><small>累计请求 / 错误</small></td>
              <td class="table-muted">${escapeHtml(formatTime(key.lastUsedAt))}</td>
              <td><button class="secondary-button compact-button" data-action="open-admin-key-drawer" data-id="${escapeHtml(key.id)}">查看</button></td>
            </tr>
          `;
        }).join("")}</tbody>
      </table>
    </div>
  `;
}

function renderAdminKeyDrawer(): string {
  const drawer = state.adminKeyDrawer;
  if (!drawer) return "";
  const keyTarget = findAdminApiKey(drawer.id);
  const user = keyTarget?.user;
  const key = keyTarget?.apiKey || null;
  if (!user || !key) return "";
  const tabs = [["overview", "概览"], ["limits", "用量与限制"], ["policy", "Key 策略"]];
  return `
    <div class="admin-detail-drawer-layer">
      <button class="admin-detail-drawer-backdrop" data-action="close-admin-key-drawer" aria-label="关闭详情"></button>
      <aside class="admin-detail-drawer" role="dialog" aria-modal="true" aria-label="API Key 详情">
        <header class="admin-detail-drawer-header">
          <div>
            <p class="eyebrow">API Key detail</p>
            <h2>${escapeHtml(key.name || key.keyPrefix || "API Key")}</h2>
            <p>${escapeHtml(`${user.name || user.email} / ${key.keyPrefix}`)}</p>
          </div>
          <button class="icon-button" data-action="close-admin-key-drawer" aria-label="关闭">×</button>
        </header>
        <nav class="admin-detail-tabs" aria-label="详情分类">
          ${tabs.map(([tab, label]) => `<button class="${drawer.tab === tab ? "active" : ""}" data-action="set-admin-key-drawer-tab" data-tab="${tab}">${label}</button>`).join("")}
        </nav>
        <div class="admin-detail-drawer-body">
          ${renderKeyDrawerBody(user, key, drawer.tab)}
        </div>
      </aside>
    </div>
  `;
}

function keyInteractionCapturePresentation(user: User, key: ApiKey) {
  const override = key.interactionCaptureEnabled;
  const userEnabled = user.interactionCaptureEnabled !== false;
  const subjectEnabled = override === null || override === undefined ? userEnabled : override;
  const globalEnabled = state.adminInteractionCapturePolicy?.enabled === true;
  const runtimeEnabled = state.adminInteractionCapturePolicy?.runtimeEnabled === true;
  const effectiveEnabled = runtimeEnabled && globalEnabled && subjectEnabled;

  let explanation: string;
  if (!runtimeEnabled) {
    explanation = "运行时采集能力未启用；此处设置会保留，但当前不会写入交互采集目录。";
  } else if (!globalEnabled) {
    explanation = "采集总开关已关闭；此处设置会保留，但当前不会写入交互采集目录。";
  } else if (override === null || override === undefined) {
    explanation = `正在跟随用户设置，用户当前已${userEnabled ? "开启" : "关闭"}交互采集。`;
  } else {
    explanation = `此 Key 已强制${override ? "开启" : "关闭"}交互采集${override === userEnabled ? "。" : `，覆盖用户当前的${userEnabled ? "开启" : "关闭"}设置。`}`;
  }

  return {
    override,
    userEnabled,
    effectiveEnabled,
    explanation
  };
}

function renderKeyDrawerBody(user: User, key: ApiKey, tab: string): string {
  const today = usageSummary(key.todayUsage);
  const week = usageSummary(key.weekUsage);
  const month = usageSummary(key.monthUsage);
  const total = usageSummary(key.totalUsage);
  const compression = key.compressionToday || { requests: 0, applied: 0, errors: 0, estimatedSavedTokens: 0 };
  const risk = key.compressionRisk || { requests: 0, failed: 0 };
  const policy = keyPolicyPresentation(key, user);
  const capture = keyInteractionCapturePresentation(user, key);
  if (tab === "limits") {
    return `
      <section class="drawer-section">
        <div class="drawer-section-heading"><div><span>Key 用量与限制</span><small>管理员强制上限与用户自设上限分开保存，最终逐项取更严格值。</small></div><button class="primary-button compact-button" data-action="edit-admin-key-usage-limits" data-id="${escapeHtml(key.id)}">编辑强制限制</button></div>
        <div class="drawer-stat-grid">${renderLimitPanel(key.usageLimit, "day", "Key 本日额度")}${renderLimitPanel(key.usageLimit, "month", "Key 本月额度")}</div>
        <article class="drawer-info-row"><span>Key 当前并发</span><strong>${key.usageLimit?.currentConcurrency || 0} / ${key.usageLimit?.limits.maxConcurrentRequests ?? "不限"}</strong></article>
        ${renderKeyLimitLayers(key.usageLimit, user.usageLimit)}
        <div class="drawer-divider"></div>
        <div class="drawer-section-heading"><div><span>账户总闸门</span><small>Key 即使设置更高额度，也不能突破该账户限制。</small></div></div>
        <div class="drawer-stat-grid">${renderLimitPanel(user.usageLimit, "day", "账户本日额度")}${renderLimitPanel(user.usageLimit, "month", "账户本月额度")}</div>
        <p class="drawer-callout">最终请求还同时受账户总限制约束。这里的费用单位是最终计费 credits，并发是正在处理的请求数，不是输入/输出 Token 上限。</p>
      </section>
    `;
  }
  if (tab === "policy") {
    return `
      <section class="drawer-section">
        <div class="drawer-section-heading"><div><span>当前生效策略</span><small>Key 按字段覆盖；没有覆盖的字段继续继承用户或系统默认。</small></div><button class="primary-button compact-button" data-action="open-policy-settings" data-scope="key" data-id="${escapeHtml(key.id)}">编辑 Key 策略</button></div>
        <div class="effective-policy-list">
          <article><span>上下文压缩</span><strong>${escapeHtml(policy.compression)}</strong><small>${escapeHtml(policy.compressionSource)}</small></article>
          <article><span>策略优化</span><strong>${escapeHtml(policy.optimization)}</strong><small>${escapeHtml(policy.optimizationSource)}</small></article>
          <article><span>请求通道</span><strong>${escapeHtml(channelDisplayName(key.channelId || user.defaultChannelId, "系统默认"))}</strong><small>${key.channelId ? "Key 覆盖" : (user.defaultChannelId ? "继承用户" : "系统默认")}</small></article>
        </div>
        <section class="key-capture-policy" aria-labelledby="key-capture-policy-title">
          <div class="key-capture-policy-heading">
            <div>
              <h3 id="key-capture-policy-title">交互采集（当前 API Key）</h3>
              <p>选择该 Key 是跟随用户设置，还是覆盖用户设置。</p>
            </div>
            <div class="key-capture-effective-status ${capture.effectiveEnabled ? "is-enabled" : "is-disabled"}">
              <span>最终状态</span>
              <strong>${capture.effectiveEnabled ? "可采集" : "不可采集"}</strong>
            </div>
          </div>
          <div class="key-capture-options" role="radiogroup" aria-label="当前 API Key 的交互采集策略">
            <button class="key-capture-option ${capture.override === null || capture.override === undefined ? "active" : ""}" role="radio" aria-checked="${capture.override === null || capture.override === undefined}" data-action="set-key-interaction-capture" data-id="${escapeHtml(key.id)}" data-mode="inherit">
              <span class="key-capture-radio" aria-hidden="true"></span>
              <span><strong>跟随用户</strong><small>用户当前：${capture.userEnabled ? "开启" : "关闭"}</small></span>
            </button>
            <button class="key-capture-option ${capture.override === true ? "active" : ""}" role="radio" aria-checked="${capture.override === true}" data-action="set-key-interaction-capture" data-id="${escapeHtml(key.id)}" data-mode="on">
              <span class="key-capture-radio" aria-hidden="true"></span>
              <span><strong>对此 Key 开启</strong><small>覆盖用户设置</small></span>
            </button>
            <button class="key-capture-option ${capture.override === false ? "active" : ""}" role="radio" aria-checked="${capture.override === false}" data-action="set-key-interaction-capture" data-id="${escapeHtml(key.id)}" data-mode="off">
              <span class="key-capture-radio" aria-hidden="true"></span>
              <span><strong>对此 Key 关闭</strong><small>覆盖用户设置</small></span>
            </button>
          </div>
          <p class="key-capture-explanation">${escapeHtml(capture.explanation)}</p>
        </section>
        <div class="drawer-stat-grid"><article class="drawer-stat"><span>今日压缩</span><strong>${compression.applied || 0} / ${compression.requests || 0}</strong><small>节省 ${fmtNum(compression.estimatedSavedTokens || 0)} tokens</small></article><article class="drawer-stat"><span>近期风险</span><strong>${risk.failed || 0} / ${risk.requests || 0}</strong><small>失败请求 / 观察请求</small></article></div>
      </section>
    `;
  }
  return `
    <section class="drawer-section">
      <div class="key-drawer-usage-grid">
        ${[["当日", today], ["当周", week], ["当月", month], ["累计", total]].map(([label, summary]) => {
          const value = summary as UsageSummary;
          return `<article class="drawer-stat"><span>${label} Token</span><strong>${fmtNum(value.totalTokens || 0)}</strong><small>${value.requests || 0} 请求 · ${value.errors || 0} 错误</small></article>`;
        }).join("")}
      </div>
      <article class="drawer-info-row"><span>策略来源</span><strong>${escapeHtml(policy.summary)}</strong></article>
      <label class="drawer-field"><span>Key 通道</span><select data-channel-assignment="api-key" data-id="${escapeHtml(key.id)}">${channelOptions(key.channelId, `继承用户通道（${channelDisplayName(user.defaultChannelId, "系统默认")}）`)}</select><button class="ghost-button compact-button" data-action="open-temporary-channel-override" data-subject-type="api_key" data-id="${escapeHtml(key.id)}" data-name="${escapeHtml(key.name || key.id)}">临时切换</button></label>
      <p class="drawer-callout">Key 通道优先于用户通道。保持“继承用户通道”时，用户通道后续变化会自动对该 Key 生效。</p>
      <article class="drawer-info-row"><span>状态</span>${pill(key.status)}</article>
      <article class="drawer-info-row"><span>最近使用</span><strong>${escapeHtml(formatTime(key.lastUsedAt))}</strong></article>
      <div class="drawer-action-row"><button class="secondary-button" data-action="toggle-admin-user-key-status" data-id="${escapeHtml(key.id)}" data-status="${key.status === "active" ? "disabled" : "active"}" ${key.status === "deleted" || key.status === "revoked" ? "disabled" : ""}>${key.status === "active" ? "停用 Key" : "启用 Key"}</button></div>
    </section>
  `;
}

function normalizeAdminKeyDrawerTab(tab?: string): "overview" | "limits" | "policy" {
  const allowed = ["overview", "limits", "policy"];
  return allowed.includes(String(tab)) ? tab as "overview" | "limits" | "policy" : "overview";
}

export function openAdminKeyDrawer(id: string, tab?: string): void {
  if (!findAdminApiKey(id)) return;
  state.adminKeyDrawer = { id, tab: normalizeAdminKeyDrawerTab(tab) };
  renderAdminUsers();
}

export function setAdminKeyDrawerTab(tab: string): void {
  if (!state.adminKeyDrawer) return;
  state.adminKeyDrawer = {
    ...state.adminKeyDrawer,
    tab: normalizeAdminKeyDrawerTab(tab)
  };
  renderAdminUsers();
}

export function closeAdminKeyDrawer(): void {
  if (!state.adminKeyDrawer) return;
  state.adminKeyDrawer = null;
  renderAdminUsers();
}

export async function editAdminUserUsageLimits(userId: string): Promise<void> {
  const user = state.adminUsers.find((item) => item.id === userId);
  if (!user) return;
  const limits = await showUsageLimitEditor({
    eyebrow: "Account hard limit",
    title: "编辑账户总限制",
    description: "账户限制是该用户网页 Chat 与全部 API Key 共同遵守的总闸门。",
    current: user.usageLimit?.limits,
    note: "关闭某一项表示该项不限制；费用按最终计费 credits 统计，不等同于 Token 数。"
  });
  if (!limits) return;
  try {
    await api(`/api/admin/users/${encodeURIComponent(userId)}/usage-limits`, {
      method: "PATCH",
      body: JSON.stringify(limits)
    });
    toast("账户用量限制已保存。", "success");
    await loadAdminUsersData();
    renderAdminUsers();
  } catch (error) { toast((error as Error).message, "error"); }
}

export async function editAdminApiKeyUsageLimits(apiKeyId: string): Promise<void> {
  const target = findAdminApiKey(apiKeyId);
  if (!target) return;
  const limits = await showUsageLimitEditor({
    eyebrow: "API Key hard limit",
    title: `编辑 ${target.apiKey.name} 的强制限制`,
    description: "管理员强制上限不会覆盖用户自己的设置；最终每一项取两层中更严格的值。",
    current: target.apiKey.usageLimit?.adminLimits,
    note: "关闭某一项表示管理员不额外限制该项，用户自设上限与账户总限制仍然有效。"
  });
  if (!limits) return;
  try {
    await api(`/api/admin/api-keys/${encodeURIComponent(apiKeyId)}/usage-limits`, {
      method: "PATCH",
      body: JSON.stringify(limits)
    });
    toast("Key 强制限制已保存。", "success");
    await loadAdminUsersData();
    renderAdminUsers();
  } catch (error) { toast((error as Error).message, "error"); }
}

function parsePolicy(raw: string | null | undefined): Record<string, any> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function compressionModeLabel(mode: string): string {
  switch (mode) {
    case "off":
      return "关闭";
    case "observe":
      return "观察模式";
    case "auto":
      return "自动压缩";
    case "force":
      return "强制压缩";
    default:
      return "继承默认";
  }
}

function optimizationPolicyLabel(policy: Record<string, any>): string {
  if (!Object.hasOwn(policy, "enabled")) return "继承默认";
  return policy.enabled ? "已开启" : "已关闭";
}

function compressionPolicyPayload(mode: string): Record<string, unknown> | null {
  const normalized = mode.trim();
  if (!normalized || normalized === "inherit") return null;
  return { mode: normalized, scope: "responses" };
}

function optimizationPolicyPayload(enabled: boolean | null): Record<string, unknown> | null {
  if (enabled === null) return null;
  if (!enabled) return { enabled: false };
  return {
    enabled: true,
    adaptiveThreshold: true,
    detailedTokenBreakdown: true,
    toolSchemaOptimization: false
  };
}

type PolicyScope = "user" | "key";

function findAdminUser(userId: string): User | undefined {
  return (state.adminUsers || []).find((user) => user.id === userId);
}

function findAdminApiKey(apiKeyId: string): { apiKey: ApiKey; user: User } | null {
  for (const user of state.adminUsers || []) {
    const apiKey = (user.apiKeys || []).find((item) => item.id === apiKeyId);
    if (apiKey) return { apiKey, user };
  }
  return null;
}

function setPolicyRadio(name: "compression-mode" | "optimization-mode", value: string): void {
  const input = document.querySelector<HTMLInputElement>(
    `#policySettingsForm input[name="${name}"][value="${CSS.escape(value)}"]`
  );
  if (input) input.checked = true;
}

function selectedPolicyRadio(name: "compression-mode" | "optimization-mode"): string {
  return document.querySelector<HTMLInputElement>(
    `#policySettingsForm input[name="${name}"]:checked`
  )?.value || "inherit";
}

function optimizationModeFromPolicy(policy: Record<string, any>): "inherit" | "off" | "on" {
  if (!Object.hasOwn(policy, "enabled")) return "inherit";
  return policy.enabled ? "on" : "off";
}

export function openPolicySettings(scope: PolicyScope, id: string): void {
  const target = scope === "user"
    ? findAdminUser(id)
    : findAdminApiKey(id);
  if (!target) {
    toast("没有找到要设置的对象。", "error");
    return;
  }

  const user = scope === "user" ? target as User : (target as { apiKey: ApiKey; user: User }).user;
  const apiKey = scope === "key" ? (target as { apiKey: ApiKey; user: User }).apiKey : null;
  // The editor must show the selected scope's own override. Showing the
  // inherited user value as if it belonged to the Key would turn inheritance
  // into an accidental Key override when the unchanged form is saved.
  const compressionPolicy = parsePolicy(scope === "key" ? apiKey?.compressionPolicy : user.compressionPolicy);
  const optimizationPolicy = parsePolicy(scope === "key" ? apiKey?.compressionOptimizationPolicy : user.compressionOptimizationPolicy);
  const compressionMode = String(compressionPolicy.mode || "inherit");

  const scopeInput = document.querySelector<HTMLInputElement>("#policy-settings-scope");
  const idInput = document.querySelector<HTMLInputElement>("#policy-settings-id");
  if (scopeInput) scopeInput.value = scope;
  if (idInput) idInput.value = id;

  if (els.policySettingsTarget) {
    els.policySettingsTarget.textContent = scope === "user"
      ? `用户: ${user.name || user.email || user.id}`
      : `API Key: ${apiKey?.name || apiKey?.keyPrefix || id} · 用户: ${user.name || user.email || user.id}`;
  }

  els.policySettingsForm?.reset();
  setPolicyRadio("compression-mode", ["off", "observe", "auto", "force"].includes(compressionMode) ? compressionMode : "inherit");
  setPolicyRadio("optimization-mode", optimizationModeFromPolicy(optimizationPolicy));
  openModal("policySettings");
}

export async function savePolicySettings(event: Event): Promise<void> {
  event.preventDefault();
  const scope = document.querySelector<HTMLInputElement>("#policy-settings-scope")?.value as PolicyScope;
  const id = document.querySelector<HTMLInputElement>("#policy-settings-id")?.value || "";
  if (!["user", "key"].includes(scope) || !id) {
    toast("策略设置对象无效。", "error");
    return;
  }

  const compressionMode = selectedPolicyRadio("compression-mode");
  const optimizationMode = selectedPolicyRadio("optimization-mode");
  const optimizationEnabled = optimizationMode === "inherit" ? null : optimizationMode === "on";

  setBusy(true);
  try {
    if (scope === "user") {
      await Promise.all([
        api(`/api/admin/users/${encodeURIComponent(id)}/compression-policy`, {
          method: "PATCH",
          body: JSON.stringify({ policy: compressionPolicyPayload(compressionMode) })
        }),
        api(`/api/admin/users/${encodeURIComponent(id)}/compression-optimization-policy`, {
          method: "PATCH",
          body: JSON.stringify({ policy: optimizationPolicyPayload(optimizationEnabled) })
        })
      ]);
    } else {
      await Promise.all([
        api(`/api/admin/api-keys/${encodeURIComponent(id)}/compression-policy`, {
          method: "PATCH",
          body: JSON.stringify({ policy: compressionPolicyPayload(compressionMode) })
        }),
        api(`/api/admin/api-keys/${encodeURIComponent(id)}/compression-optimization-policy`, {
          method: "PATCH",
          body: JSON.stringify({ policy: optimizationPolicyPayload(optimizationEnabled) })
        })
      ]);
    }
    toast("策略设置已保存。", "success");
    closeModal();
    await loadAdminUsersData();
    renderAdminUsers();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

async function refreshUsersAfterPolicyChange(message: string): Promise<void> {
  toast(message, "success");
  await loadAdminUsersData();
  renderAdminUsers();
}

export async function setUserCompression(userId: string, mode: string): Promise<void> {
  setBusy(true);
  try {
    await api(`/api/admin/users/${encodeURIComponent(userId)}/compression-policy`, {
      method: "PATCH",
      body: JSON.stringify({ policy: compressionPolicyPayload(mode) })
    });
    await refreshUsersAfterPolicyChange("用户压缩策略已更新。");
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function setKeyCompression(apiKeyId: string, mode: string): Promise<void> {
  setBusy(true);
  try {
    await api(`/api/admin/api-keys/${encodeURIComponent(apiKeyId)}/compression-policy`, {
      method: "PATCH",
      body: JSON.stringify({ policy: compressionPolicyPayload(mode) })
    });
    await refreshUsersAfterPolicyChange("Key 压缩策略已更新。");
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function setUserOptimization(userId: string, enabled: boolean | null): Promise<void> {
  setBusy(true);
  try {
    await api(`/api/admin/users/${encodeURIComponent(userId)}/compression-optimization-policy`, {
      method: "PATCH",
      body: JSON.stringify({ policy: optimizationPolicyPayload(enabled) })
    });
    await refreshUsersAfterPolicyChange("用户压缩优化策略已更新。");
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function setKeyOptimization(apiKeyId: string, enabled: boolean | null): Promise<void> {
  setBusy(true);
  try {
    await api(`/api/admin/api-keys/${encodeURIComponent(apiKeyId)}/compression-optimization-policy`, {
      method: "PATCH",
      body: JSON.stringify({ policy: optimizationPolicyPayload(enabled) })
    });
    await refreshUsersAfterPolicyChange("Key 压缩优化策略已更新。");
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export function renderAdminWallets(): void {
  if (!els.adminWalletsContent) return;
  if (state.currentView !== "wallets") return;
  const transactions = state.adminWalletTransactions || [];
  const loaded = state.adminWalletTransactionsLoaded;
  const summary = state.adminWalletTransactionSummary;
  const filters = state.adminWalletFilters;

  els.adminWalletsContent.innerHTML = `
    <section class="panel panel-fill management-panel">
      <div class="panel-heading">
        <div><p class="eyebrow">Wallet</p><h2>钱包履历</h2></div>
        <div class="panel-actions">
          <button class="secondary-button" data-action="query-admin-wallets">查询</button>
          <button class="secondary-button" data-action="export-admin-wallets">导出</button>
          <button class="secondary-button" data-action="refresh-admin">刷新</button>
        </div>
      </div>
      <section class="toolbar-grid wallet-filter-grid management-filter-grid">
        <label>
          <span>用户</span>
          <select id="admin-wallet-filter-member">
            <option value="">全部用户</option>
            ${state.adminUsers.map((user) => `
              <option value="${escapeHtml(user.id)}" ${filters.memberId === user.id ? "selected" : ""}>
                ${escapeHtml(user.name || user.email || user.id)}
              </option>
            `).join("")}
          </select>
        </label>
        <label>
          <span>开始日期</span>
          <input id="admin-wallet-filter-from" type="date" value="${escapeHtml(filters.from)}">
        </label>
        <label>
          <span>结束日期</span>
          <input id="admin-wallet-filter-to" type="date" value="${escapeHtml(filters.to)}">
        </label>
        <label>
          <span>类型</span>
          <select id="admin-wallet-filter-type">
            <option value="" ${filters.type ? "" : "selected"}>全部类型</option>
            <option value="grant" ${filters.type === "grant" ? "selected" : ""}>充值</option>
            <option value="consume" ${filters.type === "consume" ? "selected" : ""}>消费</option>
            <option value="refund" ${filters.type === "refund" ? "selected" : ""}>退款</option>
          </select>
        </label>
      </section>
      <p class="muted management-note">钱包数值按 credits 展示，1 credit = 1,000,000 microcredits。</p>
      <section class="overview-grid wallet-overview management-overview">
        <div class="overview-card">
          <p class="eyebrow">流水记录</p>
          <p class="overview-value">${loaded && summary ? fmtNum(summary.transactionCount) : "—"}</p>
        </div>
        <div class="overview-card">
          <p class="eyebrow">总充值金额</p>
          <p class="overview-value">${loaded && summary ? formatMoney(summary.totalRechargeAmount, summary.currency) : "—"}</p>
        </div>
        <div class="overview-card">
          <p class="eyebrow">总充值点数</p>
          <p class="overview-value" title="${loaded && summary ? escapeHtml(walletValueTitle(summary.totalRechargePoints)) : ""}">${loaded && summary ? escapeHtml(formatWalletCredits(summary.totalRechargePoints)) : "—"}</p>
          <p class="muted">${loaded && summary ? escapeHtml(`${fmtInt(summary.totalRechargePoints)} microcredits`) : "默认延迟加载"}</p>
        </div>
        <div class="overview-card">
          <p class="eyebrow">总消耗点数</p>
          <p class="overview-value" title="${loaded && summary ? escapeHtml(walletValueTitle(summary.totalConsumedPoints)) : ""}">${loaded && summary ? escapeHtml(formatWalletCredits(summary.totalConsumedPoints)) : "—"}</p>
          <p class="muted">${loaded && summary ? escapeHtml(`${fmtInt(summary.totalConsumedPoints)} microcredits`) : "进入钱包页后再查询"}</p>
        </div>
      </section>
      <div class="management-table-region">
        ${state.adminWalletTransactionsLoading
          ? `<p class="muted management-loading">正在加载钱包履历...</p>`
          : loaded
            ? renderWalletTransactionTable(transactions, { showMember: true, emptyText: "暂无钱包履历。" })
            : `<div class="empty-state"><p class="muted">钱包履历默认不在首屏加载。点击“查询”或“刷新”后获取最近 100 条记录。</p></div>`}
      </div>
    </section>
  `;
}

export async function queryAdminWallets(): Promise<void> {
  const memberInput = document.querySelector<HTMLSelectElement>("#admin-wallet-filter-member");
  const fromInput = document.querySelector<HTMLInputElement>("#admin-wallet-filter-from");
  const toInput = document.querySelector<HTMLInputElement>("#admin-wallet-filter-to");
  const typeInput = document.querySelector<HTMLSelectElement>("#admin-wallet-filter-type");
  state.adminWalletFilters = {
    memberId: memberInput?.value || "",
    from: fromInput?.value || "",
    to: toInput?.value || "",
    type: typeInput?.value || ""
  };
  setBusy(true);
  try {
    await loadAdminWalletTransactions();
    renderAdminWallets();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export function exportAdminWallets(): void {
  const query = buildWalletTransactionQuery(state.adminWalletFilters, 10000);
  triggerDownload(`/api/admin/wallet-transactions/export?${query}`);
}

function describeWalletTransactionType(type: string): string {
  if (type === "grant") return "充值";
  if (type === "consume") return "消费";
  if (type === "refund") return "退款";
  return type || "-";
}

function formatPointDelta(amount: number | null | undefined): string {
  const value = Number(amount || 0);
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${formatWalletCredits(value)}`;
}

function formatWalletMoney(item: WalletTransaction): string {
  if (item.cashAmount !== null && item.cashAmount !== undefined) {
    return formatMoney(item.cashAmount, item.cashCurrency || "CNY");
  }
  if (item.billedCost !== null && item.billedCost !== undefined) {
    return `${Number(item.billedCost).toFixed(6)} ${item.billingCurrency || ""}`.trim();
  }
  return "-";
}

function formatWalletTokens(item: WalletTransaction): string {
  if (item.rawTokens === null || item.rawTokens === undefined) return "-";
  const raw = fmtNum(item.rawTokens);
  if (item.pricingUnit === "legacy_token" && item.billedTokens !== null && item.billedTokens !== undefined) {
    return `${raw}<br><small class="muted">计费 ${fmtNum(item.billedTokens)}</small>`;
  }
  return raw;
}

export function renderWalletTransactionTable(
  transactions: WalletTransaction[],
  { showMember = false, emptyText = "暂无钱包履历。" } = {}
): string {
  if (!transactions.length) {
    return `<p class="muted pad">${escapeHtml(emptyText)}</p>`;
  }

  return `
    <div class="table-wrap">
      <table class="sub-table wallet-history-table">
        <thead>
          <tr>
            <th>时间</th>
            ${showMember ? "<th>用户</th>" : ""}
            <th>类型</th>
            <th>金额</th>
            <th>变动 (credits)</th>
            <th>Token</th>
            <th>余额 (credits)</th>
            <th>模型 / 备注</th>
            ${showMember ? "<th>操作人</th>" : ""}
          </tr>
        </thead>
        <tbody>
          ${transactions.map((item) => `
            <tr>
              <td class="table-muted">${escapeHtml(formatTime(item.createdAt))}</td>
              ${showMember ? `<td>${escapeHtml(item.member?.name || item.member?.email || item.memberId || "-")}<br><small class="muted">${escapeHtml(item.member?.email || item.memberId || "")}</small></td>` : ""}
              <td>${escapeHtml(describeWalletTransactionType(item.type))}</td>
              <td>${escapeHtml(formatWalletMoney(item))}</td>
              <td><strong>${formatPointDelta(item.amount)}</strong></td>
              <td>${formatWalletTokens(item)}</td>
              <td title="${escapeHtml(walletValueTitle(item.balanceAfter || 0))}">${escapeHtml(formatWalletCredits(item.balanceAfter || 0))}</td>
              <td class="table-muted">
                ${item.model ? `${escapeHtml(item.model)}<br>` : ""}
                <small>${escapeHtml(item.note || item.orderId || item.requestId || "-")}</small>
              </td>
              ${showMember ? `<td class="table-muted">${escapeHtml(item.operator?.name || item.operator?.email || item.operatorMemberId || "-")}</td>` : ""}
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>
  `;
}

export function renderAdminGpt(): void {
  if (!els.adminGptContent) return;
  if (state.currentView !== "gpt") return;
  const oauth: Partial<OauthStatus> = state.adminOauth || {};
  const modelManagement = state.publicModelManagement;
  const publicModels = state.publicModels;
  const modelManagementNotice = modelManagement?.dynamicEnabled
    ? `
        <div class="notice notice-success">
          <strong>公共模型目录已启用:</strong> SeekToken 控制库负责模型准入与费率；执行由直连 Channel 选择服务实例和节点。
        </div>
      `
    : `
        <div class="notice notice-info">
          <strong>公共模型目录不可用:</strong> 请检查 SeekToken 控制库和模型费率存储。
        </div>
      `;
  const failoverRows = renderAccountFailoverRows(state.chatGptFailoverEvents || []);
  els.adminGptContent.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">OAuth</p><h2>chatgpt-bridge OAuth 状态</h2></div>
        <button class="secondary-button" data-action="refresh-admin">刷新</button>
      </div>
      <div class="user-info">
        <p><strong>auth.json 路径</strong> ${escapeHtml(oauth.authPath || "?")}</p>
        <p><strong>文件存在</strong> ${oauth.exists ? "✅" : "❌"}</p>
        <p><strong>access_token</strong> ${oauth.hasAccessToken ? "✅ 存在" : "❌ 缺失"}</p>
        <p><strong>refresh_token</strong> ${oauth.hasRefreshToken ? "✅ 存在" : "❌ 缺失"}</p>
        <p><strong>过期时间</strong> ${oauth.expiresAt ? new Date(oauth.expiresAt).toLocaleString("zh-CN") : "未知"} ${oauth.expired ? "(已过期)" : ""}</p>
        <p><strong>剩余有效期</strong> ${oauth.expiresIn !== null && oauth.expiresIn !== undefined ? `${Math.floor(oauth.expiresIn / 60)} 分钟` : "未知"}</p>
        <p><strong>账户 ID</strong> ${escapeHtml(oauth.accountId || "未知")}</p>
        ${oauth.error ? `<p class="form-error">错误: ${escapeHtml(oauth.error)}</p>` : ""}
      </div>
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Config</p><h2>chatgpt-bridge 配置</h2></div>
      </div>
      <div class="docs-content">
        <h3>环境变量</h3>
        <pre><code>CHATGPT_BRIDGE_URL=http://127.0.0.1:10531/v1
CHATGPT_BRIDGE_AUTH_PATH=~/.codex/auth.json
HTTPS_PROXY=http://127.0.0.1:7897  # Clash 代理(需 OpenAI 支持的地区)</code></pre>
        <h3>OAuth 登录</h3>
        <p>在本地有浏览器的电脑执行 <code>npx @openai/codex login</code>,分别登录每个 ChatGPT 订阅账号。登录成功后复制对应 <code>~/.codex/auth.json</code> 内容,粘贴到下方账号池。</p>
        <h3>ChatGPT 账号池</h3>
        <p class="muted">这里仅管理账号凭据。账号必须配置到服务实例的执行节点后才会承载流量，不再存在全局活跃账号。</p>
        <div class="account-add-form" style="margin:16px 0;">
          <input type="text" id="account-label-input" placeholder="账号标签(如 主账号)" style="width:200px;" class="search-input">
          <textarea id="account-authjson-input" placeholder="粘贴 auth.json 内容..." style="width:100%;min-height:80px;margin-top:8px;" class="search-input"></textarea>
          <button class="secondary-button" data-action="add-account" style="margin-top:8px;">添加账号</button>
        </div>
        ${state.chatGptAccounts.length
          ? `<div class="table-wrap"><table class="sub-table">
              <thead><tr><th>标签</th><th>状态</th><th>access_token</th><th>过期时间</th><th>账户 ID</th><th>操作</th></tr></thead>
              <tbody>
                ${state.chatGptAccounts.map((a) => {
                  const s = a.status || {};
                  const expiry = s.expiresAt ? new Date(s.expiresAt).toLocaleString("zh-CN") : "未知";
                  return `
                  <tr>
                    <td><strong>${escapeHtml(a.label)}</strong></td>
                    <td>${s.error ? `<span class="pill pill-warning">错误</span>` : (s.expired ? '<span class="pill pill-warning">已过期</span>' : '<span class="pill">正常</span>')}</td>
                    <td>${s.hasAccessToken ? "✅" : "❌"} ${s.hasRefreshToken ? "🔄" : ""}</td>
                    <td><small class="muted">${escapeHtml(expiry)}${s.expiresIn !== null && s.expiresIn !== undefined ? ` (${Math.floor(s.expiresIn / 60)}分钟)` : ""}</small></td>
                    <td><small class="muted">${escapeHtml(s.accountId || "未知")}</small></td>
                    <td>
                      <button class="ghost-button danger-button" data-action="delete-account" data-id="${escapeHtml(a.id)}" data-name="${escapeHtml(a.label)}">删除</button>
                    </td>
                  </tr>
                `}).join("")}
              </tbody>
            </table></div>`
          : `<p class="muted">暂无账号。添加凭据后，请在服务实例中创建账号执行节点。</p>`
        }
      </div>
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Audit</p><h2>历史全局账号切换日志</h2></div>
        <button class="secondary-button" data-action="refresh-admin">刷新</button>
      </div>
      <div class="docs-content">
        ${failoverRows}
      </div>
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Models</p><h2>公共模型目录</h2></div>
        <button class="secondary-button" data-action="refresh-admin">刷新</button>
      </div>
      <div class="docs-content">
        ${modelManagementNotice}
        <p class="muted">目录由 SeekToken 控制库提供。新增模型会原子写入公开目录和点数费率；请求执行时把公开模型名映射为上游模型名，再由 Channel 选择直连节点。</p>
        <div class="panel-actions" style="margin:16px 0;">
          <button class="secondary-button" data-action="add-model">新增模型</button>
        </div>
        ${publicModels.length
          ? `<div class="table-wrap"><table class="sub-table">
              <thead><tr><th>模型名</th><th>上游模型</th><th>API Base</th><th>来源</th><th>操作</th></tr></thead>
              <tbody>
                ${publicModels.map((m) => `
                  <tr>
                    <td><code>${escapeHtml(m.modelName)}</code></td>
                    <td><code>${escapeHtml(m.upstreamModel)}</code></td>
                    <td><small class="muted">${escapeHtml(m.apiBase || "默认")}</small></td>
                    <td><span class="pill">SeekToken 目录</span></td>
                    <td>${m.isDeletable ? `<button class="ghost-button danger-button" data-action="delete-model" data-id="${escapeHtml(m.id)}" data-name="${escapeHtml(m.modelName)}">删除</button>` : '<small class="muted">只读</small>'}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table></div>`
          : `<p class="muted">暂无公共模型；请新增模型并填写其上游模型名和精确点数费率。</p>`
        }
      </div>
    </section>
  `;
}

function renderAccountFailoverRows(events: AccountFailoverEvent[]): string {
  if (!events.length) {
    return `<p class="muted">暂无自动切换记录。</p>`;
  }
  const statusLabel = (status: string): string => {
    if (status === "retried_success") return `<span class="pill">重试成功</span>`;
    if (status === "switched") return `<span class="pill">已切换</span>`;
    if (status === "no_candidate") return `<span class="pill pill-warning">无备用账号</span>`;
    if (status === "reload_failed" || status === "retried_failed") {
      return `<span class="pill pill-warning">${escapeHtml(status)}</span>`;
    }
    return `<span class="muted">${escapeHtml(status)}</span>`;
  };

  return `
    <div class="table-wrap"><table class="sub-table">
      <thead><tr><th>时间</th><th>入口</th><th>触发原因</th><th>账号</th><th>状态</th><th>reload / retry</th><th>错误</th></tr></thead>
      <tbody>
        ${events.map((event) => `
          <tr>
            <td><small class="muted">${escapeHtml(formatTime(event.createdAt))}</small></td>
            <td><code>${escapeHtml(event.route)}</code></td>
            <td>${escapeHtml(event.trigger)}</td>
            <td>
              <small class="muted">${escapeHtml(event.fromAccountLabel || event.fromAccountId || "未知")}</small>
              <br><span class="muted">→</span>
              <small class="muted">${escapeHtml(event.toAccountLabel || event.toAccountId || "无")}</small>
            </td>
            <td>${statusLabel(event.status)}</td>
            <td><small class="muted">${escapeHtml(event.reloadStatus || "-")} / ${escapeHtml(event.retryStatus || "-")}</small></td>
            <td><small class="muted">${event.errorStatus ? `${event.errorStatus} ` : ""}${escapeHtml(event.errorCode || "")}<br>${escapeHtml(event.message || event.errorMessage || "")}</small></td>
          </tr>
        `).join("")}
      </tbody>
    </table></div>
  `;
}

export function renderAdminHealth(): void {
  if (!els.adminHealthContent) return;
  if (state.currentView !== "health") return;
  const health: { timestamp: string; layers: Array<{ name: string; ok: boolean; latencyMs: number | null; error: string | null; url: string; expiresAt?: string | null }> } =
    state.adminHealth || { timestamp: "", layers: [] };
  els.adminHealthContent.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Health</p><h2>上游链路健康检查</h2></div>
        <button class="secondary-button" data-action="refresh-admin">刷新</button>
      </div>
      <div class="list">
        ${health.layers?.map((l) => `
          <div class="list-item">
            <div>
              <strong>${escapeHtml(l.name)}</strong>
              <span class="pill ${l.ok ? "" : "pill-warning"}">${l.ok ? "正常" : "异常"}</span>
              ${l.latencyMs ? `<span class="muted">${l.latencyMs}ms</span>` : ""}
              <br><small class="muted">${escapeHtml(l.url || "")}</small>
              ${l.error ? `<br><small class="form-error">${escapeHtml(l.error)}</small>` : ""}
              ${l.expiresAt ? `<br><small class="muted">过期: ${new Date(l.expiresAt).toLocaleString("zh-CN")}</small>` : ""}
            </div>
          </div>
        `).join("") || "<p class='muted pad'>加载中...</p>"}
      </div>
      <div class="docs-content">
        <p class="muted">检查时间: ${health.timestamp ? new Date(health.timestamp).toLocaleString("zh-CN") : "?"}</p>
      </div>
    </section>
  `;
}

function escapeRegex(value: string): string {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildExactModelPattern(model: string): string {
  return `^${escapeRegex(model)}(?:$|[-_])`;
}

function buildBillingRateRows(
  rates: ModelBillingRate[] = [],
  models: PublicModel[] = []
): ModelBillingRateRow[] {
  const normalizedRates = rates.map((rate) => ({ ...rate, configured: true as const }));
  const configuredModels = new Set(normalizedRates.map((rate) => rate.model.toLowerCase()));
  const missingModels = models
    .filter((model) => !model.isInternalRoute)
    .map((model) => String(model.modelName || "").trim())
    .filter(Boolean)
    .filter((modelName, index, list) => list.indexOf(modelName) === index)
    .filter((modelName) => !configuredModels.has(modelName.toLowerCase()))
    .sort((left, right) => left.localeCompare(right))
    .map((modelName) => ({
      model: modelName,
      modelPattern: buildExactModelPattern(modelName),
      input: 0,
      cachedInput: 0,
      output: 0,
      configured: false as const
    }));
  return [...normalizedRates, ...missingModels];
}

export function renderAdminProducts(): void {
  const container = document.querySelector<HTMLElement>("#adminProductsContent");
  if (!container) return;
  if (state.currentView !== "products") return;

  const plans = state.pricingPlans || [];
  const billingRateRows = buildBillingRateRows(
    state.billingModelRates?.rates || [],
    state.publicModels || []
  );

  container.innerHTML = `
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Billing</p><h2>公共模型点数费率</h2><p class="muted">所有通道与物理实例统一继承公共模型费率，不在此重复配置内部路由。</p></div>
        <button class="secondary-button" data-action="refresh-admin">刷新</button>
      </div>
      ${state.billingModelRates?.ignoredInternalRateCount
        ? `<div class="notice notice-info">已忽略 ${state.billingModelRates.ignoredInternalRateCount} 条历史内部路由费率；下次保存公共费率时会自动清理。</div>`
        : ""}
      ${billingRateRows.length
        ? `<div class="table-wrap">
            <table class="sub-table">
              <thead>
                <tr>
                  <th>模型</th>
                  <th>匹配规则</th>
                  <th>Input / 1M</th>
                  <th>Cached / 1M</th>
                  <th>Output / 1M</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                ${billingRateRows.map((rate) => `
                  <tr>
                    <td>
                      <strong>${escapeHtml(rate.model)}</strong>
                      ${rate.configured ? "" : '<span class="pill pill-warning">未配置</span>'}
                    </td>
                    <td><code class="key-value">${escapeHtml(rate.modelPattern)}</code></td>
                    <td>${rate.configured ? rate.input : "-"}</td>
                    <td>${rate.configured ? rate.cachedInput : "-"}</td>
                    <td>${rate.configured ? rate.output : "-"}</td>
                    <td><button class="ghost-button" data-action="edit-billing-rate" data-id="${escapeHtml(rate.model)}">${rate.configured ? "编辑" : "配置"}</button></td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>`
        : `<p class="muted pad">暂无模型费率配置，也未发现已注册模型。</p>`}
    </section>
    <section class="panel panel-fill">
      <div class="panel-heading">
        <div><p class="eyebrow">Products</p><h2>定价方案管理</h2></div>
        <div class="panel-actions">
          <button class="secondary-button" data-action="refresh-admin">刷新</button>
          <button class="primary-button" data-action="create-product">新增方案</button>
        </div>
      </div>
      ${plans.length
        ? `<div class="table-wrap">
            <table class="sub-table">
              <thead>
                <tr>
                  <th>名称</th>
                  <th>描述</th>
                  <th>金额</th>
                  <th>商品类型</th>
                  <th>点数</th>
                  <th>状态</th>
                  <th>排序</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                ${plans.map((plan) => `
                  <tr>
                    <td>
                      <strong>${escapeHtml(plan.name)}</strong>
                      ${plan.isFeatured ? '<span class="pill">推荐</span>' : ""}
                    </td>
                    <td class="table-muted">${escapeHtml(plan.description || "-")}</td>
                    <td>
                      ${plan.price === 0 ? "免费" : `实付 ${formatMoney(plan.price, plan.currency)}`}
                      <br><small class="muted">票面 ${formatMoney(plan.faceAmount ?? plan.price, plan.currency)}</small>
                    </td>
                    <td>
                      ${escapeHtml(describeProductType(plan.productType))}
                      ${plan.productType === "monthly_pass" ? `<br><small class="muted">${plan.validityMonths || 1} 个自然月</small>` : ""}
                    </td>
                    <td>${fmtNum(plan.tokenAmount)}</td>
                    <td>${pill(plan.isActive ? "active" : "disabled")}</td>
                    <td>${plan.sortOrder}</td>
                    <td>
                      <button class="ghost-button" data-action="edit-product" data-id="${escapeHtml(plan.id)}">编辑</button>
                      <button class="ghost-button" data-action="${plan.isActive ? "disable-product" : "enable-product"}" data-id="${escapeHtml(plan.id)}">
                        ${plan.isActive ? "停用" : "启用"}
                      </button>
                      <button class="ghost-button danger-button" data-action="delete-product" data-id="${escapeHtml(plan.id)}" data-name="${escapeHtml(plan.name)}">删除</button>
                    </td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>`
        : `<p class="muted pad">暂无定价方案。</p>`}
    </section>
  `;
}

function describeProductType(type: string | null | undefined): string {
  return type === "monthly_pass" ? "月卡" : "充值包";
}

function describeEntitlementStatus(status: string): string {
  const labels: Record<string, string> = {
    purchased: "待启用",
    active: "使用中",
    exhausted: "已耗尽",
    expired: "已过期",
    revoked: "已撤销"
  };
  return labels[status] || status;
}

function entitlementSnapshotName(entitlement: MemberEntitlement): string {
  const name = entitlement.entitlementSnapshot?.name;
  return typeof name === "string" && name.trim() ? name : "月卡";
}

function renderFundingSource(sourceType: string): string {
  return sourceType === "monthly_pass" ? "月卡" : "钱包";
}

function renderEntitlementTransactions(transactions: EntitlementTransaction[]): string {
  if (!transactions.length) return `<p class="muted pad">暂无月卡消费流水。</p>`;
  return `
    <div class="table-wrap">
      <table class="sub-table">
        <thead><tr><th>时间</th><th>权益</th><th>用户</th><th>请求</th><th>类型</th><th>金额</th><th>消费后</th></tr></thead>
        <tbody>${transactions.map((item) => `
          <tr>
            <td>${escapeHtml(formatTime(item.createdAt))}</td>
            <td><code class="key-value">${escapeHtml(item.entitlementId)}</code></td>
            <td>${escapeHtml(item.memberId)}</td>
            <td><code class="key-value">${escapeHtml(item.requestId || "-")}</code></td>
            <td>${escapeHtml(item.type)}</td>
            <td>${formatWalletCredits(item.amount)}</td>
            <td>${formatWalletCredits(item.consumedAfter)}</td>
          </tr>
        `).join("")}</tbody>
      </table>
    </div>
  `;
}

function renderFundingAllocations(allocations: UsageFundingAllocation[]): string {
  if (!allocations.length) return `<p class="muted pad">暂无资金分摊记录。</p>`;
  return `
    <div class="table-wrap">
      <table class="sub-table">
        <thead><tr><th>时间</th><th>请求</th><th>来源</th><th>状态</th><th>预占</th><th>结算</th><th>释放</th><th>到期</th></tr></thead>
        <tbody>${allocations.map((item) => `
          <tr>
            <td>${escapeHtml(formatTime(item.createdAt))}</td>
            <td><code class="key-value">${escapeHtml(item.requestId)}</code></td>
            <td>${escapeHtml(renderFundingSource(item.sourceType))}${item.sourceId ? `<br><code class="key-value">${escapeHtml(item.sourceId)}</code>` : ""}</td>
            <td>${escapeHtml(item.status)}</td>
            <td>${formatWalletCredits(item.reservedPoints)}</td>
            <td>${formatWalletCredits(item.settledPoints)}</td>
            <td>${formatWalletCredits(item.releasedPoints)}</td>
            <td>${escapeHtml(formatTime(item.expiresAt))}</td>
          </tr>
        `).join("")}</tbody>
      </table>
    </div>
  `;
}

function renderFundingEvents(events: BillingFundingEvent[]): string {
  if (!events.length) return `<p class="muted pad">暂无资金异常事件。</p>`;
  return `
    <div class="table-wrap">
      <table class="sub-table">
        <thead><tr><th>时间</th><th>类型</th><th>请求</th><th>用户</th><th>说明</th></tr></thead>
        <tbody>${events.map((item) => `
          <tr>
            <td>${escapeHtml(formatTime(item.createdAt))}</td>
            <td>${escapeHtml(item.type)}</td>
            <td><code class="key-value">${escapeHtml(item.requestId || "-")}</code></td>
            <td>${escapeHtml(item.memberId || "-")}</td>
            <td>${escapeHtml(item.message || item.code || "-")}</td>
          </tr>
        `).join("")}</tbody>
      </table>
    </div>
  `;
}

export function renderAdminEntitlements(): void {
  const container = document.querySelector<HTMLElement>("#adminEntitlementsContent");
  if (!container) return;
  if (state.currentView !== "entitlements") return;
  const entitlements = state.adminEntitlements || [];
  const operations = state.adminMonthlyPassOperations;
  const entitlementOps = operations?.summary.entitlements;
  const fundingOps = operations?.summary.funding;
  const activeCount = entitlements.filter((item) => item.status === "active").length;
  const pendingCount = entitlements.filter((item) => item.status === "purchased").length;
  const totalAvailable = entitlements
    .filter((item) => item.status === "active")
    .reduce((sum, item) => sum + Number(item.availablePoints || 0), 0);

  container.innerHTML = `
    <section class="panel panel-fill management-panel">
      <div class="panel-heading">
        <div><p class="eyebrow">Monthly Pass</p><h2>月卡权益</h2></div>
        <button class="secondary-button" data-action="refresh-admin">刷新</button>
      </div>
      <section class="overview-grid wallet-overview management-overview">
        <div class="overview-card"><p class="eyebrow">权益总数</p><p class="overview-value">${fmtNum(entitlements.length)}</p></div>
        <div class="overview-card"><p class="eyebrow">使用中</p><p class="overview-value">${fmtNum(entitlementOps?.activeCount ?? activeCount)}</p></div>
        <div class="overview-card"><p class="eyebrow">待启用</p><p class="overview-value">${fmtNum(entitlementOps?.pendingCount ?? pendingCount)}</p><p class="muted">${entitlementOps ? formatMoney(entitlementOps.pendingAmount, entitlementOps.currency) : "—"}</p></div>
        <div class="overview-card"><p class="eyebrow">活动剩余额度</p><p class="overview-value">${formatWalletCredits(entitlementOps?.activeAvailablePoints ?? totalAvailable)}</p></div>
        <div class="overview-card"><p class="eyebrow">过期未用额度</p><p class="overview-value">${formatWalletCredits(entitlementOps?.expiredUnusedPoints || 0)}</p></div>
        <div class="overview-card"><p class="eyebrow">月卡消费</p><p class="overview-value">${formatWalletCredits(fundingOps?.monthlyPassSettledPoints || 0)}</p></div>
        <div class="overview-card"><p class="eyebrow">钱包补差</p><p class="overview-value">${formatWalletCredits(fundingOps?.walletSettledPoints || 0)}</p></div>
        <div class="overview-card"><p class="eyebrow">混合扣款请求</p><p class="overview-value">${fmtNum(fundingOps?.mixedRequestCount || 0)}</p></div>
        <div class="overview-card"><p class="eyebrow">超时预占</p><p class="overview-value">${fmtNum(fundingOps?.expiredReservedAllocationCount || 0)}</p><p class="muted">已释放 ${formatWalletCredits(fundingOps?.releasedPoints || 0)}</p></div>
        <div class="overview-card"><p class="eyebrow">资金不足</p><p class="overview-value">${fmtNum(fundingOps?.insufficientFundsCount || 0)}</p></div>
        <div class="overview-card"><p class="eyebrow">资金短缺异常</p><p class="overview-value">${fmtNum(fundingOps?.fundingShortfallCount || 0)}</p></div>
      </section>
      <div class="management-table-region">
        ${entitlements.length
          ? `<div class="table-wrap">
            <table class="sub-table">
              <thead>
                <tr>
                  <th>权益 ID</th>
                  <th>用户</th>
                  <th>套餐</th>
                  <th>状态</th>
                  <th>额度</th>
                  <th>购买 / 启用 / 到期</th>
                  <th>订单</th>
                </tr>
              </thead>
              <tbody>
                ${entitlements.map((item) => `
                  <tr>
                    <td><code class="key-value">${escapeHtml(item.id)}</code></td>
                    <td>${escapeHtml(item.memberId)}</td>
                    <td>${escapeHtml(entitlementSnapshotName(item))}</td>
                    <td>${escapeHtml(describeEntitlementStatus(item.status))}</td>
                    <td>
                      <strong>${formatWalletCredits(item.availablePoints)}</strong>
                      <br><small class="muted">总 ${formatWalletCredits(item.totalPoints)} · 已用 ${formatWalletCredits(item.consumedPoints)} · 预占 ${formatWalletCredits(item.reservedPoints)}</small>
                    </td>
                    <td class="table-muted">
                      ${escapeHtml(formatTime(item.purchasedAt))}
                      <br>${escapeHtml(formatTime(item.activatedAt))}
                      <br>${escapeHtml(formatTime(item.expiresAt))}
                    </td>
                    <td><code class="key-value">${escapeHtml(item.orderId)}</code></td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>`
          : `<p class="muted pad">暂无月卡权益。</p>`}
      </div>
    </section>
    <section class="panel panel-fill management-panel">
      <div class="panel-heading"><div><p class="eyebrow">Audit</p><h2>月卡消费流水</h2></div></div>
      ${renderEntitlementTransactions(operations?.entitlementTransactions || [])}
    </section>
    <section class="panel panel-fill management-panel">
      <div class="panel-heading"><div><p class="eyebrow">Funding</p><h2>资金分摊记录</h2></div></div>
      ${renderFundingAllocations(operations?.fundingAllocations || [])}
    </section>
    <section class="panel panel-fill management-panel">
      <div class="panel-heading"><div><p class="eyebrow">Events</p><h2>资金异常与清理事件</h2></div></div>
      ${renderFundingEvents(operations?.fundingEvents || [])}
    </section>
  `;
}

function parseBillingRateField(value: string): number | null {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return null;
  return numeric;
}

export async function editBillingRate(model: string): Promise<void> {
  const rate = state.billingModelRates?.rates.find((item) => item.model === model) || {
    model,
    modelPattern: buildExactModelPattern(model),
    input: 0,
    cachedInput: 0,
    output: 0
  };

  const modelInput = document.querySelector<HTMLInputElement>("#billing-rate-model");
  const modelPatternInput = document.querySelector<HTMLInputElement>("#billing-rate-model-pattern");
  const inputField = document.querySelector<HTMLInputElement>("#billing-rate-input");
  const cachedInputField = document.querySelector<HTMLInputElement>("#billing-rate-cached-input");
  const outputField = document.querySelector<HTMLInputElement>("#billing-rate-output");
  if (!modelInput || !modelPatternInput || !inputField || !cachedInputField || !outputField) return;

  modelInput.value = rate.model;
  modelPatternInput.value = rate.modelPattern;
  inputField.value = String(rate.input);
  cachedInputField.value = String(rate.cachedInput);
  outputField.value = String(rate.output);
  if (els.billingRateTarget) {
    els.billingRateTarget.textContent = state.billingModelRates?.rates.some((item) => item.model === model)
      ? `${rate.model} · ${rate.modelPattern}`
      : `${rate.model} · 未配置，保存后新增`;
  }
  openModal("billingRate");
}

export async function saveBillingRate(event?: Event): Promise<void> {
  event?.preventDefault();
  const config = state.billingModelRates || { version: "default", rates: [] };
  const model = document.querySelector<HTMLInputElement>("#billing-rate-model")?.value.trim() || "";
  const modelPattern = document.querySelector<HTMLInputElement>("#billing-rate-model-pattern")?.value.trim() || buildExactModelPattern(model);
  const inputField = document.querySelector<HTMLInputElement>("#billing-rate-input");
  const cachedInputField = document.querySelector<HTMLInputElement>("#billing-rate-cached-input");
  const outputField = document.querySelector<HTMLInputElement>("#billing-rate-output");
  const rate = config.rates.find((item) => item.model === model);
  if (!model || !inputField || !cachedInputField || !outputField) return;

  const input = parseBillingRateField(inputField.value);
  const cachedInput = parseBillingRateField(cachedInputField.value);
  const output = parseBillingRateField(outputField.value);
  if (input === null || cachedInput === null || output === null) {
    toast("模型费率必须是非负数字。", "error");
    return;
  }

  const updatedRate: ModelBillingRate = {
    ...(rate || { model, modelPattern }),
    model,
    modelPattern,
    input,
    cachedInput,
    output
  };

  setBusy(true);
  try {
    const rates = rate
      ? config.rates.map((item) => item.model === model ? updatedRate : item)
      : [...config.rates, updatedRate].sort((left, right) => left.model.localeCompare(right.model));
    state.billingModelRates = await api<BillingModelRates>("/api/admin/billing/model-rates", {
      method: "PUT",
      body: JSON.stringify({
        version: new Date().toISOString(),
        rates
      })
    });
    toast(rate ? "模型点数费率已更新。" : "模型点数费率已新增。", "success");
    closeModal();
    renderAdminProducts();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export function openCreateModelModal(): void {
  const modelNameInput = document.querySelector<HTMLInputElement>("#create-model-name");
  const upstreamInput = document.querySelector<HTMLInputElement>("#create-model-upstream");
  const inputField = document.querySelector<HTMLInputElement>("#create-model-rate-input");
  const cachedInputField = document.querySelector<HTMLInputElement>("#create-model-rate-cached-input");
  const outputField = document.querySelector<HTMLInputElement>("#create-model-rate-output");
  if (modelNameInput) modelNameInput.value = "";
  if (upstreamInput) upstreamInput.value = "";
  if (inputField) inputField.value = "";
  if (cachedInputField) cachedInputField.value = "";
  if (outputField) outputField.value = "";
  openModal("createModel");
}

export async function createPublicModel(event?: Event): Promise<void> {
  event?.preventDefault();
  const modelNameInput = document.querySelector<HTMLInputElement>("#create-model-name");
  const upstreamInput = document.querySelector<HTMLInputElement>("#create-model-upstream");
  const inputField = document.querySelector<HTMLInputElement>("#create-model-rate-input");
  const cachedInputField = document.querySelector<HTMLInputElement>("#create-model-rate-cached-input");
  const outputField = document.querySelector<HTMLInputElement>("#create-model-rate-output");
  if (!modelNameInput || !inputField || !cachedInputField || !outputField) return;

  const modelName = modelNameInput.value.trim();
  const upstreamModel = upstreamInput?.value.trim() || modelName;
  const input = parseBillingRateField(inputField.value);
  const cachedInput = parseBillingRateField(cachedInputField.value);
  const output = parseBillingRateField(outputField.value);

  if (!modelName) {
    toast("请输入模型名。", "error");
    return;
  }
  if (input === null || cachedInput === null || output === null) {
    toast("请完整填写模型点数费率。", "error");
    return;
  }

  setBusy(true);
  try {
    await api("/api/admin/models", {
      method: "POST",
      body: JSON.stringify({
        modelName,
        upstreamModel,
        billingRate: { input, cachedInput, output }
      })
    });
    toast(`模型 "${modelName}" 已添加，并已写入点数费率。`, "success");
    closeModal();
    await Promise.allSettled([loadAdminGptData(), loadAdminProductsData()]);
    renderAdminGpt();
    renderAdminProducts();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export function renderAdminOrders(): void {
  const container = document.querySelector<HTMLElement>("#adminOrdersContent");
  if (!container) return;
  if (state.currentView !== "orders") return;

  const orders = state.adminOrders || [];
  const totalAmount = orders.reduce((sum, o) => sum + Number(o.amount || 0), 0);
  const settledOrders = orders.filter((o) => ["paid", "fulfilled"].includes(o.status));
  const settledAmount = settledOrders.reduce((sum, o) => sum + Number(o.amount || 0), 0);

  container.innerHTML = `
    <section class="panel panel-fill management-panel">
      <div class="panel-heading">
        <div><p class="eyebrow">Orders</p><h2>订单管理</h2></div>
        <div class="panel-actions">
          <button class="secondary-button" data-action="refresh-admin">刷新</button>
          <a href="/api/admin/orders/export" class="secondary-button" download>导出订单</a>
        </div>
      </div>
      <section class="overview-grid wallet-overview management-overview orders-overview">
        <div class="overview-card">
          <p class="eyebrow">订单总数</p>
          <p class="overview-value">${fmtNum(orders.length)}</p>
        </div>
        <div class="overview-card">
          <p class="eyebrow">已支付订单</p>
          <p class="overview-value">${fmtNum(settledOrders.length)}</p>
        </div>
        <div class="overview-card">
          <p class="eyebrow">订单总金额</p>
          <p class="overview-value">¥${fmtNum(totalAmount)}</p>
        </div>
        <div class="overview-card">
          <p class="eyebrow">已收款</p>
          <p class="overview-value">¥${fmtNum(settledAmount)}</p>
        </div>
      </section>
      <div class="management-table-region">
        ${orders.length
          ? `<div class="table-wrap">
            <table class="sub-table">
              <thead>
                <tr>
                  <th>订单 ID</th>
                  <th>用户</th>
                  <th>方案</th>
                  <th>金额</th>
                  <th>点数</th>
                  <th>状态</th>
                  <th>支付方式</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                ${orders.map((order) => `
                  <tr>
                    <td><code class="key-value">${escapeHtml(order.id)}</code></td>
                    <td>${escapeHtml(order.member?.name || order.member?.email || order.memberId || "-")}</td>
                    <td>${escapeHtml(order.plan?.name || "-")}</td>
                    <td>¥${order.amount}</td>
                    <td>${fmtNum(order.tokenAmount)}</td>
                    <td>${pill(order.status)}</td>
                    <td class="table-muted">${escapeHtml(order.paymentMethod || "-")}</td>
                    <td class="table-muted">${escapeHtml(formatTime(order.createdAt))}</td>
                    <td>
                      ${["pending", "payment_pending", "payment_failed"].includes(order.status) ? `<button class="secondary-button" data-action="reconcile-order-payment" data-id="${escapeHtml(order.id)}">查支付</button>` : ""}
                      ${["pending", "payment_pending"].includes(order.status) ? `<button class="secondary-button" data-action="mark-order-paid" data-id="${escapeHtml(order.id)}">标记已支付</button>` : ""}
                      ${["paid", "fulfilled"].includes(order.status) ? `<button class="ghost-button danger-button" data-action="refund-order" data-id="${escapeHtml(order.id)}">退款</button>` : ""}
                    </td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
            </div>`
          : `<p class="muted pad">暂无订单。</p>`}
      </div>
    </section>
  `;
}

// ===== Modal / UI 工具 =====

export function openModal(name: "apiKey" | "createUser" | "rechargeUser" | "policySettings" | "billingRate" | "pricingPlan" | "createModel" | "key"): void {
  const target: Record<string, HTMLElement | null> = {
    apiKey: els.apiKeyModal,
    createUser: els.createUserModal,
    rechargeUser: els.rechargeUserModal,
    policySettings: els.policySettingsModal,
    billingRate: els.billingRateModal,
    pricingPlan: els.pricingPlanModal,
    createModel: els.createModelModal,
    key: els.keyModal
  };

  const el = target[name];
  if (!el) return;

  els.modals.forEach((modal) => modal.classList.add("hidden"));
  el.classList.remove("hidden");
  els.modalBackdrop?.classList.remove("hidden");
  els.modalBackdrop?.setAttribute("aria-hidden", "false");

  const firstInput = el.querySelector<HTMLInputElement>("input, select, textarea") || el.querySelector<HTMLButtonElement>("button");
  firstInput?.focus();
}

export function closeModal(): void {
  els.modals.forEach((modal) => modal.classList.add("hidden"));
  els.modalBackdrop?.classList.add("hidden");
  els.modalBackdrop?.setAttribute("aria-hidden", "true");
}

export function setBusy(isBusy: boolean): void {
  document.querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("button, input, select, textarea").forEach((item) => {
    item.disabled = isBusy;
  });
}

let toastTimer: number | undefined;

export function toast(message: string, type = ""): void {
  if (!els.toast) return;
  els.toast.textContent = message;
  els.toast.classList.remove("hidden", "error", "success");
  if (type) els.toast.classList.add(type);
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    els.toast?.classList.add("hidden");
    els.toast?.classList.remove("error", "success");
  }, 3600);
}

export async function copyText(text: string, successMessage: string): Promise<void> {
  if (!text) {
    toast("没有可复制内容。");
    return;
  }

  try {
    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      copyTextWithFallback(text);
    }
    toast(successMessage);
  } catch {
    try {
      copyTextWithFallback(text);
      toast(successMessage);
    } catch {
      toast("复制失败，请手动选择内容复制。", "error");
    }
  }
}

function copyTextWithFallback(text: string): void {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    const copied = document.execCommand("copy");
    if (!copied) {
      throw new Error("execCommand copy failed");
    }
  } finally {
    document.body.removeChild(textarea);
  }
}

function parsePlanFeaturesText(value: string): string {
  const items = String(value || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  return JSON.stringify(items);
}

function stringifyPlanFeatures(value: string | null | undefined): string {
  if (!value) return "";
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item || "").trim()).filter(Boolean).join("\n");
    }
  } catch {
    /* ignore */
  }
  return String(value);
}

function readPricingPlanFormInput() {
  const planId = document.querySelector<HTMLInputElement>("#pricing-plan-id")?.value.trim() || "";
  const name = document.querySelector<HTMLInputElement>("#pricing-plan-name")?.value.trim() || "";
  const description = document.querySelector<HTMLTextAreaElement>("#pricing-plan-description")?.value.trim() || "";
  const price = Number(document.querySelector<HTMLInputElement>("#pricing-plan-price")?.value || 0);
  const faceAmountRaw = document.querySelector<HTMLInputElement>("#pricing-plan-face-amount")?.value || "";
  const faceAmount = faceAmountRaw === "" ? price : Number(faceAmountRaw);
  const tokenAmount = Number(document.querySelector<HTMLInputElement>("#pricing-plan-token-amount")?.value || 0);
  const productType = document.querySelector<HTMLSelectElement>("#pricing-plan-product-type")?.value || "wallet_topup";
  const validityMonthsRaw = document.querySelector<HTMLInputElement>("#pricing-plan-validity-months")?.value || "";
  const activationWindowDaysRaw = document.querySelector<HTMLInputElement>("#pricing-plan-activation-window-days")?.value || "";
  const validityMonths = productType === "monthly_pass" ? Number(validityMonthsRaw || 1) : null;
  const activationWindowDays = productType === "monthly_pass" && activationWindowDaysRaw !== ""
    ? Number(activationWindowDaysRaw)
    : null;
  const sortOrder = Number(document.querySelector<HTMLInputElement>("#pricing-plan-sort-order")?.value || 0);
  const features = parsePlanFeaturesText(
    document.querySelector<HTMLTextAreaElement>("#pricing-plan-features")?.value || ""
  );
  const isFeatured = document.querySelector<HTMLInputElement>("#pricing-plan-featured")?.checked === true;
  const isActive = document.querySelector<HTMLInputElement>("#pricing-plan-active")?.checked === true;

  if (!name) {
    toast("请输入方案名称。", "error");
    return null;
  }
  if (!Number.isFinite(price) || price < 0) {
    toast("实付金额必须是非负数字。", "error");
    return null;
  }
  if (!Number.isFinite(faceAmount) || faceAmount < 0) {
    toast("票面金额必须是非负数字。", "error");
    return null;
  }
  if (!Number.isFinite(tokenAmount) || tokenAmount < 0) {
    toast("点数必须是非负数字。", "error");
    return null;
  }
  if (productType === "monthly_pass" && (!Number.isSafeInteger(validityMonths || 0) || Number(validityMonths) <= 0)) {
    toast("月卡有效月数必须是正整数。", "error");
    return null;
  }
  if (activationWindowDays !== null && (!Number.isSafeInteger(activationWindowDays) || activationWindowDays <= 0)) {
    toast("最晚启用天数必须是正整数。", "error");
    return null;
  }
  if (!Number.isFinite(sortOrder) || sortOrder < 0) {
    toast("排序必须是非负数字。", "error");
    return null;
  }

  return {
    planId,
    payload: {
      name,
      description,
      price,
      faceAmount,
      tokenAmount,
      productType,
      validityMonths,
      activationWindowDays,
      currency: "CNY",
      interval: "one-time",
      features,
      isFeatured,
      isActive,
      sortOrder
    }
  };
}

function fillPricingPlanForm(plan?: PricingPlan | null): void {
  const planIdInput = document.querySelector<HTMLInputElement>("#pricing-plan-id");
  const nameInput = document.querySelector<HTMLInputElement>("#pricing-plan-name");
  const descriptionInput = document.querySelector<HTMLTextAreaElement>("#pricing-plan-description");
  const priceInput = document.querySelector<HTMLInputElement>("#pricing-plan-price");
  const faceAmountInput = document.querySelector<HTMLInputElement>("#pricing-plan-face-amount");
  const tokenAmountInput = document.querySelector<HTMLInputElement>("#pricing-plan-token-amount");
  const productTypeInput = document.querySelector<HTMLSelectElement>("#pricing-plan-product-type");
  const validityMonthsInput = document.querySelector<HTMLInputElement>("#pricing-plan-validity-months");
  const activationWindowDaysInput = document.querySelector<HTMLInputElement>("#pricing-plan-activation-window-days");
  const sortOrderInput = document.querySelector<HTMLInputElement>("#pricing-plan-sort-order");
  const featuresInput = document.querySelector<HTMLTextAreaElement>("#pricing-plan-features");
  const featuredInput = document.querySelector<HTMLInputElement>("#pricing-plan-featured");
  const activeInput = document.querySelector<HTMLInputElement>("#pricing-plan-active");
  if (!planIdInput || !nameInput || !descriptionInput || !priceInput || !faceAmountInput || !tokenAmountInput || !productTypeInput || !validityMonthsInput || !activationWindowDaysInput || !sortOrderInput || !featuresInput || !featuredInput || !activeInput) {
    return;
  }

  planIdInput.value = plan?.id || "";
  nameInput.value = plan?.name || "";
  descriptionInput.value = plan?.description || "";
  priceInput.value = plan ? String(plan.price) : "";
  faceAmountInput.value = plan ? String(plan.faceAmount ?? plan.price) : "";
  tokenAmountInput.value = plan ? String(plan.tokenAmount) : "";
  productTypeInput.value = plan?.productType || "wallet_topup";
  validityMonthsInput.value = plan?.validityMonths ? String(plan.validityMonths) : "";
  activationWindowDaysInput.value = plan?.activationWindowDays ? String(plan.activationWindowDays) : "";
  sortOrderInput.value = plan ? String(plan.sortOrder) : String((state.pricingPlans?.length || 0) + 1);
  featuresInput.value = stringifyPlanFeatures(plan?.features);
  featuredInput.checked = Boolean(plan?.isFeatured);
  activeInput.checked = plan ? Boolean(plan.isActive) : true;
}

export function copyPlainKey(): Promise<void> {
  const text = els.plainKey?.textContent?.trim() ?? "";
  return copyText(text, "API Key 已复制。");
}

export function copyApiKey(apiKeyId: string): Promise<void> {
  const apiKey = findApiKey(apiKeyId);
  const plainKey = state.plainApiKeys[apiKeyId] || apiKey?.plainKey;

  if (plainKey) {
    return copyText(plainKey, "完整 API Key 已复制。");
  }

  return copyText(apiKey?.keyPrefix || "", "只复制了 Key 前缀；请先点击「获取完整 Key」。");
}

export async function fetchAdminApiKey(apiKeyId: string): Promise<void> {
  setBusy(true);
  try {
    const data = await api<{ plainKey: string }>(`/admin/api-keys/${encodeURIComponent(apiKeyId)}/plain`);
    state.plainApiKeys[apiKeyId] = data.plainKey;
    saveStoredPlainApiKeys(state.plainApiKeys);
    toast("API Key 已获取。", "success");
    renderMembers();
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export function toggleShowAdminKey(apiKeyId: string): void {
  delete state.plainApiKeys[apiKeyId];
  saveStoredPlainApiKeys(state.plainApiKeys);
  renderMembers();
}

export async function createPricingPlan(): Promise<void> {
  fillPricingPlanForm(null);
  if (els.pricingPlanModalTitle) {
    els.pricingPlanModalTitle.textContent = "新增定价方案";
  }
  if (els.pricingPlanModalSubmit) {
    els.pricingPlanModalSubmit.textContent = "创建方案";
  }
  openModal("pricingPlan");
}

export async function editPricingPlan(planId: string): Promise<void> {
  const plan = state.pricingPlans.find((p) => p.id === planId);
  if (!plan) return;
  fillPricingPlanForm(plan);
  if (els.pricingPlanModalTitle) {
    els.pricingPlanModalTitle.textContent = "编辑定价方案";
  }
  if (els.pricingPlanModalSubmit) {
    els.pricingPlanModalSubmit.textContent = "保存方案";
  }
  openModal("pricingPlan");
}

export async function savePricingPlan(event?: Event): Promise<void> {
  event?.preventDefault();
  const formInput = readPricingPlanFormInput();
  if (!formInput) return;

  const { planId, payload } = formInput;
  const method = planId ? "PATCH" : "POST";
  const url = planId
    ? `/api/admin/pricing-plans/${encodeURIComponent(planId)}`
    : "/api/admin/pricing-plans";

  setBusy(true);
  try {
    const data = await api<{ plan: PricingPlan }>(url, {
      method,
      body: JSON.stringify(payload)
    });
    if (planId) {
      state.pricingPlans = state.pricingPlans.map((item) =>
        item.id === planId ? data.plan : item
      );
      toast("定价方案已更新。", "success");
    } else {
      state.pricingPlans = [...state.pricingPlans, data.plan]
        .sort((left, right) => Number(left.sortOrder || 0) - Number(right.sortOrder || 0));
      toast("定价方案已创建。", "success");
    }
    closeModal();
    renderAdminProducts();
    void loadAdminProductsData()
      .then(() => renderAdminProducts())
      .catch((error) => console.error("refresh admin products failed:", error));
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function toggleProductStatus(planId: string, isActive: boolean): Promise<void> {
  setBusy(true);
  try {
    const data = await api<{ plan: PricingPlan }>(`/api/admin/pricing-plans/${encodeURIComponent(planId)}`, {
      method: "PATCH",
      body: JSON.stringify({ isActive })
    });
    state.pricingPlans = state.pricingPlans.map((item) =>
      item.id === planId ? data.plan : item
    );
    toast(isActive ? "定价方案已启用。" : "定价方案已停用。", "success");
    renderAdminProducts();
    void loadAdminProductsData()
      .then(() => renderAdminProducts())
      .catch((error) => console.error("refresh admin products failed:", error));
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function deletePricingPlan(planId: string, planName: string): Promise<void> {
  if (!confirm(`确定要删除定价方案「${planName}」吗？`)) return;

  setBusy(true);
  try {
    await api(`/api/admin/pricing-plans/${encodeURIComponent(planId)}`, {
      method: "DELETE"
    });
    state.pricingPlans = state.pricingPlans.filter((item) => item.id !== planId);
    toast("定价方案已删除。", "success");
    renderAdminProducts();
    void loadAdminProductsData()
      .then(() => renderAdminProducts())
      .catch((error) => console.error("refresh admin products failed:", error));
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function markOrderPaid(orderId: string): Promise<void> {
  if (!confirm("确定要标记此订单为已支付吗？系统将自动为用户充值对应点数。")) return;

  setBusy(true);
  try {
    await api(`/api/admin/orders/${encodeURIComponent(orderId)}/mark-paid`, {
      method: "POST",
      body: JSON.stringify({ paymentMethod: "manual" })
    });
    toast("订单已标记为已支付，点数已充值。", "success");
    await loadAdminOrdersData();
    renderAdminOrders();
    void Promise.allSettled([loadAdminUsersData(), loadAdminWalletTransactions()]).then(() => {
      renderAdminUsers();
      renderAdminWallets();
    });
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function refundOrder(orderId: string): Promise<void> {
  const reason = prompt("请输入退款原因：");
  if (!reason) return;

  if (!confirm("确定要退款此订单吗？")) return;

  setBusy(true);
  try {
    await api(`/api/admin/orders/${encodeURIComponent(orderId)}/refund`, {
      method: "POST",
      body: JSON.stringify({ reason })
    });
    toast("订单已退款。", "success");
    await loadAdminOrdersData();
    renderAdminOrders();
    void Promise.allSettled([loadAdminUsersData(), loadAdminWalletTransactions()]).then(() => {
      renderAdminUsers();
      renderAdminWallets();
    });
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}

export async function reconcileOrderPayment(orderId: string): Promise<void> {
  setBusy(true);
  try {
    await api(`/api/admin/orders/${encodeURIComponent(orderId)}/reconcile-payment`, {
      method: "POST",
      body: JSON.stringify({})
    });
    toast("支付状态已查询。", "success");
    await loadAdminOrdersData();
    renderAdminOrders();
    void Promise.allSettled([loadAdminUsersData(), loadAdminWalletTransactions()]).then(() => {
      renderAdminUsers();
      renderAdminWallets();
    });
  } catch (error) {
    toast((error as Error).message, "error");
  } finally {
    setBusy(false);
  }
}
